<template>
	<div class="dbMaintenanceRoot">
		<div class="pageHeading">SQL Server Maintenance</div>

		<div v-if="error" class="errorBox">{{error}}</div>

		<div v-if="!permissionRead" class="errorBox">You do not have the Online Server Maintenance Read permission.</div>
		<template v-else>
			<!-- ============================================================================================ -->
			<!-- Feature state: persistent dry-run banner and configuration notices                            -->
			<!-- ============================================================================================ -->
			<div v-if="feature && feature.dryRun" class="dryRunBanner" role="alert">
				<div class="dryRunTitle">
					DRY-RUN MODE — this instance is not the authorized orchestrator<template v-if="feature.dryRunReason"> ({{feature.dryRunReason}})</template>.
					Nothing this page does will touch live servers.
				</div>
				<div class="dryRunDetail">
					This machine: <code class="inline">{{feature.thisMachine || 'unknown'}}</code>.
					Authorized orchestrator machine: <code class="inline">{{feature.orchestratorMachine || '(none configured)'}}</code>.
					Read-only checks (health, configuration discovery) are {{dryRunReadOnlyOpsText}}.
				</div>
			</div>

			<!-- ============================================================================================ -->
			<!-- Needs-attention / interrupted banner (the spec's "prominent banner rather than guessing")       -->
			<!-- ============================================================================================ -->
			<div v-if="attentionRun" class="attentionBanner" role="alert" id="dbmAttention">
				<div class="attentionTitle">
					<span class="stateBadge badgeBad big">{{runStatusLabel(attentionRun.status)}}</span>
					<span>
						{{runKindLabel(attentionRun.kind)}}<template v-if="attentionRun.actor"> started by {{attentionRun.actor}}</template>
						<template v-if="attentionRun.status === 'Interrupted'"> was interrupted</template><template v-else> needs your attention</template>
						— {{formatDateTime(attentionRun.finishedUtc || attentionRun.startedUtc)}}<template v-if="attentionRun.currentStepName">, step “{{attentionRun.currentStepName}}”</template>.
					</span>
				</div>
				<div class="attentionBody">
					<template v-if="attentionRun.status === 'Interrupted'">
						The orchestrator stopped while this run was in flight (TdsAppService crashed, was stopped, or was updated).  The platform is
						normally stable in this state — just not necessarily in its desired configuration.  Review the operation panel, then use
						<em>Resume interrupted migration</em> or the other actions to bring it back to the desired state.
					</template>
					<template v-else>
						The automation could not complete this run safely and has parked it.  Follow the manual fix below, verify the platform
						status above, then acknowledge.
					</template>
				</div>
				<pre v-if="attentionRun.manualFixInstructions" class="attentionFix">{{attentionRun.manualFixInstructions}}</pre>
				<div v-else-if="attentionRun.error" class="attentionFix">{{attentionRun.error}}</div>
				<div class="attentionActions">
					<button type="button" class="linkButton attentionLink" @click="showRun(null)">Open the operation panel</button>
					<template v-if="permissionWrite && attentionRun.canAcknowledge">
						<span v-if="controlBusy === 'Acknowledge'" class="loadingInline"><ScaleLoader /> Acknowledging…</span>
						<slide-button v-else
									  :key="'slide_ack_' + attentionRun.runId + '_' + slideCounter"
									  :auto-width="false"
									  :circle="true"
									  :noanimate="true"
									  :width="260"
									  :height="44"
									  text="slide to acknowledge"
									  success-text="acknowledging"
									  :disabled="anyActionInFlight"
									  :requireReleaseToComplete="true"
									  @completed="controlRun('Acknowledge')" />
						<span class="mutedText">Acknowledging clears this banner; the run stays in the history.</span>
					</template>
				</div>
			</div>

			<div v-if="featureNotices.length" class="featureStrip">
				<div v-for="(n, i) in featureNotices" :key="i" class="featureNotice" :class="n.cssClass">
					<span>{{n.text}}</span>
					<a v-if="n.linkText" href="#" class="featureNoticeLink" @click.prevent="scrollTo(n.anchor)">{{n.linkText}}</a>
				</div>
			</div>

			<!-- ============================================================================================ -->
			<!-- Status toolbar                                                                                -->
			<!-- ============================================================================================ -->
			<div class="card toolbarCard">
				<button type="button" class="gradientButton lightBlue" @click="refreshAll" :disabled="loading">Refresh</button>
				<span v-if="loading" class="loadingInline"><ScaleLoader /> Checking web servers and database servers…</span>
				<span v-else-if="lastRefreshed" class="mutedText">
					Status as of {{lastRefreshed}} — last checked {{lastCheckedAgo}}<template v-if="autoRefreshPaused"> — auto-refresh paused while idle</template><template v-if="status && status.fromCache"> — from the short-lived status cache</template>
				</span>
				<span v-if="!permissionWrite" class="readOnlyBadge" title="You do not have the Online Server Maintenance Write permission.">read-only</span>
				<span v-if="feature && feature.serviceVersion" class="mutedText versionText" title="TdsAppService version">v{{feature.serviceVersion}}</span>
			</div>

			<template v-if="status">
				<!-- ======================================================================================== -->
				<!-- Overall verdict and desired state                                                         -->
				<!-- ======================================================================================== -->
				<div class="card verdictCard" :class="verdict.cardClass">
					<div class="verdictRow">
						<span class="verdictBadge" :class="verdict.badgeClass">{{verdict.label}}</span>
						<span class="verdictDetail">{{platform && platform.verdictDetail ? platform.verdictDetail : verdict.defaultDetail}}</span>
					</div>
					<div class="desiredLine">
						<strong>Desired:</strong> all web apps on <code class="inline">{{platform && platform.desiredDbServer ? platform.desiredDbServer : 'unknown'}}</code> (primary).
						<strong>Active:</strong> <code class="inline" :class="{ activeMismatch: activeMismatch }">{{activeDbServerText}}</code><span v-if="platform && platform.activeDbServerSource" class="mutedText"> ({{platform.activeDbServerSource}})</span>
					</div>
					<div v-if="platform && !platform.fleetAgrees && platform.fleetDisagreement" class="warningBox">{{platform.fleetDisagreement}}</div>
					<div v-if="platform && platform.recommendedAction" class="recommendedRow">
						<button type="button" class="gradientButton tdsHealthBrown primaryActionButton"
								:disabled="!!actionDisabledReason(recommendedKind) || anyActionInFlight"
								:title="actionDisabledReason(recommendedKind) || 'Opens a confirmation listing exactly what will happen, in order.'"
								@click="beginAction(recommendedKind)">{{platform.recommendedAction}}</button>
						<span v-if="actionBusy === recommendedKind" class="loadingInline"><ScaleLoader /> Preparing the preview…</span>
						<span v-else-if="actionDisabledReason(recommendedKind)" class="disabledReason">{{actionDisabledReason(recommendedKind)}}</span>
						<span v-else class="readyReason">Runs “{{runKindLabel(recommendedKind)}}” as configured below, after a confirmation that lists every step.</span>
					</div>
					<ul v-if="platform && platform.warnings && platform.warnings.length" class="warningList">
						<li v-for="(w, i) in platform.warnings" :key="i">{{w}}</li>
					</ul>
					<div v-if="platform && (platform.environmentName || platform.appName)" class="mutedText envLine">
						Environment: {{platform.environmentName || '?'}}<template v-if="platform.appName"> · app: {{platform.appName}}</template>
					</div>
				</div>

				<!-- ======================================================================================== -->
				<!-- Operation in progress: live run panel, run lock, stale-lock override                        -->
				<!-- ======================================================================================== -->
				<div class="card runCard" id="dbmRun" :class="{ runCardAttention: displayedRun && displayedRun.status === 'NeedsAttention', runCardIdle: !displayedRun && !(runLock && runLock.held) }">
					<div class="secondaryHeading">{{runCardHeading}}</div>
					<div v-if="selectedRunLoading" class="loadingInline"><ScaleLoader /> Loading run {{selectedRunLoading}}…</div>
					<RunProgressPanel v-if="displayedRun"
									  :run="displayedRun"
									  :viewingHistory="!!selectedRun"
									  :permissionWrite="permissionWrite"
									  :controlBusy="controlBusy"
									  :disabled="anyActionInFlight"
									  :slideCounter="slideCounter"
									  @control="controlRun"
									  @back="showRun(null)" />
					<div v-else class="mutedText idleText">
						No maintenance operation is running.<template v-if="history.length">  Click a run in the history below to review it here.</template>
					</div>
					<div v-if="runNote" class="noteBox">{{runNote}}</div>

					<div v-if="runLock && runLock.held" class="lockBlock" :class="{ lockStale: runLock.stale }">
						<div class="lockHead">
							<strong>Run lock held</strong> by <strong>{{runLock.ownerDisplayName || runLock.actor || 'unknown'}}</strong>
							<span v-if="runLock.stale" class="stateBadge badgeBad" title="The heartbeat is older than the stale threshold, or the owning process is not alive on this machine.">STALE — heartbeat {{lockHeartbeatAge}} ago</span>
							<span v-else class="stateBadge badgeGood" title="The owner refreshed the lock heartbeat recently.">heartbeat {{lockHeartbeatAge}} ago</span>
							<span v-if="runLock.ownedByOtherInstance" class="stateBadge badgeBad" title="The lock owner is a different TdsAppService process or machine.">another instance</span>
						</div>
						<div class="kvGrid lockGrid">
							<span class="kvKey">Operation</span><span>{{runLock.operation || '—'}}<template v-if="runLock.runId"> · run <code class="inline">{{runLock.runId}}</code></template></span>
							<span class="kvKey">Actor</span><span>{{runLock.actor || '—'}}</span>
							<span class="kvKey">Owner process</span><span><code class="inline">{{runLock.ownerMachine || '?'}}</code> PID {{runLock.ownerPid || '?'}}<template v-if="runLock.ownerVersion"> · version {{runLock.ownerVersion}}</template><template v-if="runLock.ownerProcessStartUtc"> · process started {{formatDateTime(runLock.ownerProcessStartUtc)}}</template></span>
							<span class="kvKey">Acquired</span><span>{{formatDateTime(runLock.acquiredUtc)}}</span>
							<span class="kvKey">Last heartbeat</span><span>{{formatDateTime(runLock.heartbeatUtc)}}</span>
						</div>
						<div v-if="runLock.ownedByOtherInstance" class="errorBox lockOtherInstance">
							This lock belongs to a <strong>different TdsAppService process or machine</strong> ({{runLock.ownerMachine || '?'}}, PID {{runLock.ownerPid || '?'}}).
							Another instance may be orchestrating maintenance right now.  Do not start anything from this page until you know which
							instance is the real orchestrator — the authorized machine is <code class="inline">{{feature && feature.orchestratorMachine ? feature.orchestratorMachine : '(none configured)'}}</code>.
						</div>
						<div v-if="runLock.stale" class="lockOverrideRow">
							<template v-if="permissionWrite">
								<span v-if="lockOverrideBusy" class="loadingInline"><ScaleLoader /> Overriding the lock…</span>
								<button v-else type="button" class="gradientButton lightBlue" :disabled="anyActionInFlight" @click="overrideLock">Override stale lock…</button>
								<span class="disabledReason">The owner stopped heart-beating.  Overriding requires typing the owner's name and is logged; do it only when you are sure the owner is gone.</span>
							</template>
							<span v-else class="disabledReason">The lock is stale; an administrator with the write permission can override it.</span>
						</div>
					</div>
				</div>

				<!-- ======================================================================================== -->
				<!-- Web servers                                                                               -->
				<!-- ======================================================================================== -->
				<div class="card" v-if="platform">
					<div class="secondaryHeading">Web servers</div>
					<p class="hintText">
						Each web server is asked, through its TdsAppReleaseAgent, which database servers its web apps are <em>actually</em> connected to
						(effective), which ones its AppSettings file names (configured), and whether Kent, NowlinAdmin, SQL Server and the ShadowSvr
						search engine respond.  When every server agrees and is healthy, they are summarized on one line.
					</p>
					<div v-if="webServers.length === 0" class="mutedText">No web servers reported.</div>
					<template v-else>
						<div v-if="showAggregate" class="aggregateRow">
							<span class="statusDot dotGreen"></span>
							<span class="aggregateText" :title="platform.aggregate && platform.aggregate.summary ? platform.aggregate.summary : ''">{{aggregateText}}</span>
							<button type="button" class="linkButton" @click="webServersExpanded = !webServersExpanded">{{webServersExpanded ? 'Hide individual servers' : 'Show individual servers'}}</button>
						</div>
						<div v-else class="warningBox">{{webServersProblemSummary}}</div>
						<div v-if="webServersListVisible" class="serverList">
							<div v-for="ws in webServers" :key="ws.agentId || ws.agentName" class="serverRow">
								<div class="serverHead">
									<span class="statusDot" :class="webServerDotClass(ws)" :title="webServerDotTitle(ws)"></span>
									<span class="serverName">{{ws.agentName || ws.agentId}}</span>
									<span v-if="!ws.connected" class="stateBadge badgeBad" title="The TdsAppReleaseAgent on this server is not connected to TdsAppService.">agent disconnected</span>
									<span v-else-if="ws.agentLacksRpc" class="stateBadge badgeWarn" title="This TdsAppReleaseAgent is too old to answer GetDbCutoverStatus.  Update the agent.">agent needs update</span>
									<span v-if="ws.releaseName" class="mutedText">release {{ws.releaseName}}</span>
									<span v-if="ws.connected && !ws.coherent" class="stateBadge badgeBad" :title="ws.coherenceError || 'The installed release is incoherent.'">INCOHERENT</span>
									<span v-if="ws.maintenanceModeActive" class="stateBadge badgeWarn">maintenance mode</span>
									<span v-if="ws.startupPending" class="stateBadge badgeWarn">startup pending</span>
									<span v-if="isLatent(ws)" class="stateBadge badgeLatent" title="AppSettings names a different database server than the one the web apps are connected to.  The change takes effect on the next app pool recycle.">written but NOT recycled — latent cutover</span>
								</div>
								<div v-if="ws.connected && !ws.agentLacksRpc" class="serverConfig">
									<div>
										<span class="kvKey">Effective (live connections):</span>
										taylor → <code class="inline">{{ws.effectiveTaylorServer || '?'}}</code><template v-if="ws.effectiveTaylorDb"> / {{ws.effectiveTaylorDb}}</template>,
										umls → <code class="inline">{{ws.effectiveUmlsServer || '?'}}</code><template v-if="ws.effectiveUmlsDb"> / {{ws.effectiveUmlsDb}}</template>
									</div>
									<div :class="{ configMismatchText: isLatent(ws) }">
										<span class="kvKey">Configured (AppSettings):</span>
										taylor → <code class="inline">{{ws.configuredTaylorServer || '?'}}</code>,
										umls → <code class="inline">{{ws.configuredUmlsServer || '?'}}</code><template v-if="isLatent(ws)"> — differs from the effective servers; takes effect only after an app pool recycle</template>
									</div>
								</div>
								<div v-if="ws.connected && !ws.agentLacksRpc" class="healthChips">
									<span class="chip" :class="chipClass(ws.kentResponded && ws.kentHealthy)">Kent {{chipText(ws.kentResponded, ws.kentHealthy)}}</span>
									<span class="chip" :class="chipClass(ws.adminResponded && ws.adminHealthy)">Admin {{chipText(ws.adminResponded, ws.adminHealthy)}}</span>
									<span class="chip" :class="chipClass(ws.sqlTaylorOk)">SQL taylor {{ws.sqlTaylorOk ? 'OK' : 'FAIL'}}</span>
									<span class="chip" :class="chipClass(ws.sqlUmlsOk)">SQL umls {{ws.sqlUmlsOk ? 'OK' : 'FAIL'}}</span>
									<span class="chip" :class="chipClass(ws.shadowSvrOk)">ShadowSvr {{ws.shadowSvrOk ? 'OK' : 'FAIL'}}</span>
									<span v-if="ws.sqlProductVersion" class="mutedText">SQL Server {{ws.sqlProductVersion}}</span>
								</div>
								<ul v-if="ws.problems && ws.problems.length" class="problemList">
									<li v-for="(p, i) in ws.problems" :key="i">{{p}}</li>
								</ul>
								<div v-else-if="ws.summary && !ws.healthy" class="problemText">{{ws.summary}}</div>
								<div class="serverMeta mutedText">
									<template v-if="ws.appVersion">app {{ws.appVersion}}<template v-if="ws.buildDateStr"> ({{ws.buildDateStr}})</template> · </template>
									<template v-if="ws.agentVersion">agent {{ws.agentVersion}} · </template>
									<template v-if="ws.lastCutoverStep">last cutover step: {{ws.lastCutoverStep}} · </template>
									<template v-if="ws.checkedUtc">checked {{formatUtc(ws.checkedUtc)}}</template>
									<span v-if="ws.appSettingsSha256" :title="'AppSettings SHA-256: ' + ws.appSettingsSha256"> · AppSettings {{ws.appSettingsSha256.substring(0, 12)}}…</span>
								</div>
							</div>
						</div>
					</template>
				</div>

				<!-- ======================================================================================== -->
				<!-- Database servers                                                                          -->
				<!-- ======================================================================================== -->
				<div class="card" v-if="platform">
					<div class="secondaryHeading">Database servers</div>
					<p class="hintText">
						Both servers are checked directly from TdsAppService (SQL Server version and the state of each maintained database) and
						through the DbServerAgent running on each server (service health and free space on the data drive).  The <strong>ACTIVE</strong>
						server is the one the web apps are connected to; in the desired state that is the primary.  A database in RESTORING state on
						the server that is <em>not</em> active is normal leftover from an earlier migration and is shown muted.
					</p>
					<div class="dbServerGrid">
						<div v-for="db in dbServers" :key="db.role" class="dbServerCard" :class="{ dbActive: db.isActive }">
							<div class="dbServerHead">
								<span class="roleTag">{{db.role}}</span>
								<span class="serverName">{{db.name || '(not configured)'}}</span>
								<span v-if="db.isActive" class="stateBadge badgeActive" title="The web apps are effectively connected to this server.">ACTIVE</span>
								<span v-if="isDesired(db)" class="stateBadge badgeOff" title="The desired state is every web app connected to this server.">desired</span>
							</div>
							<div v-if="db.missing" class="mutedText">This endpoint is not configured — see Configuration below.</div>
							<template v-else>
								<div v-if="db.host && db.host !== db.name" class="mutedText hostLine">{{db.host}}</div>
								<div class="kvGrid">
									<span class="kvKey">SQL Server</span>
									<span>
										<span class="statusDot" :class="db.sqlReachable ? 'dotGreen' : 'dotRed'"></span>
										<template v-if="db.sqlReachable">reachable — version {{db.productVersion || '?'}}<template v-if="db.sqlLatencyMs != null">, {{db.sqlLatencyMs}} ms</template></template>
										<template v-else><span class="problemText">unreachable</span><template v-if="db.sqlError">: {{db.sqlError}}</template></template>
									</span>
									<span class="kvKey">DbServerAgent</span>
									<div>
										<span class="statusDot" :class="db.agentReachable ? (db.agentHealthy ? 'dotGreen' : 'dotAmber') : 'dotRed'"></span>
										<template v-if="!db.agentReachable"><span class="problemText">unreachable</span></template>
										<template v-else-if="db.agentHealthy">healthy</template>
										<template v-else><span class="problemText">reports problems</span></template>
										<pre v-if="db.agentHealthDetail && db.agentHealthDetail !== 'OK'" class="agentDetail">{{db.agentHealthDetail}}</pre>
									</div>
									<span class="kvKey">Data drive free</span>
									<span :class="{ freeSpaceLow: db.freeSpaceAboveFloor === false }">
										{{formatBytes(db.dataDriveFreeBytes)}}<template v-if="db.freeSpaceAboveFloor === false"> — BELOW the configured floor</template><template v-else-if="db.freeSpaceAboveFloor === true"> (above the configured floor)</template>
									</span>
								</div>
								<table v-if="db.databases && db.databases.length" class="dbTable">
									<thead>
										<tr><th>Database</th><th>State</th><th>Recovery model</th><th>Note</th></tr>
									</thead>
									<tbody>
										<tr v-for="d in db.databases" :key="d.name" :class="{ dbInformational: d.informational }">
											<td>{{d.name}}</td>
											<td><span class="dbState" :class="dbStateClass(d)">{{d.state || '?'}}</span></td>
											<td>{{d.recoveryModel || '—'}}</td>
											<td class="mutedText">{{d.note || ''}}</td>
										</tr>
									</tbody>
								</table>
								<div v-else class="mutedText">No database state reported.</div>
								<div v-if="db.checkedUtc" class="mutedText serverMeta">checked {{formatUtc(db.checkedUtc)}}</div>
							</template>
						</div>
					</div>
				</div>
			</template>
			<div v-else-if="loading" class="card loadingRow"><ScaleLoader /> Loading status…</div>

			<!-- ============================================================================================ -->
			<!-- Configure the migration (direction, databases, cutover target) — used by the checks and later phases -->
			<!-- ============================================================================================ -->
			<div class="card" id="dbmConfigure">
				<div class="secondaryHeading">Configure the migration</div>
				<p class="hintText">
					A migration copies the selected databases from the <strong>source</strong> (the server the web apps are using now) to the
					<strong>target</strong>.  <em>Migrate + cut over</em> then repoints every web server at the target, which is only allowed when
					every web-app database (tagged <em>web app</em> below) is selected; <em>Migrate only</em> leaves the web servers where they
					are, for any selection.  The direction defaults to <em>active server → the other server</em>; the precondition checks below
					verify it.
				</p>
				<div v-if="!endpointNames.length" class="mutedText">The database server endpoints are not configured — see Configuration below.</div>
				<template v-else>
					<div class="formRow directionRow">
						<label class="formLabel" for="dbmSource">Source</label>
						<select id="dbmSource" class="directionSelect" v-model="direction.source" :disabled="anyActionInFlight" @change="onDirectionChanged('source')">
							<option v-if="!direction.source" :value="null" disabled>— choose —</option>
							<option v-for="n in endpointNames" :key="'s' + n" :value="n">{{endpointOptionLabel(n)}}</option>
						</select>
						<button type="button" class="swapButton" title="Swap source and target" :disabled="anyActionInFlight || !direction.source || !direction.target" @click="swapDirection">⇄</button>
						<label class="formLabel" for="dbmTarget">Target</label>
						<select id="dbmTarget" class="directionSelect" v-model="direction.target" :disabled="anyActionInFlight" @change="onDirectionChanged('target')">
							<option v-if="!direction.target" :value="null" disabled>— choose —</option>
							<option v-for="n in endpointNames" :key="'t' + n" :value="n">{{endpointOptionLabel(n)}}</option>
						</select>
					</div>
					<div v-if="directionNote" class="warningBox">{{directionNote}}</div>
					<div class="formRow dbListHead">
						<span class="formLabel">Databases</span>
						<span v-if="sourceDatabasesLoading" class="loadingInline mutedText"><ScaleLoader /> Listing the databases on {{direction.source}}…</span>
						<span v-else-if="sourceDatabasesServer" class="mutedText">on {{sourceDatabasesServer}}</span>
					</div>
					<div v-if="sourceDatabasesNote" class="mutedText dbListNote">{{sourceDatabasesNote}}</div>
					<div v-if="sourceDatabases.length" class="dbCheckList">
						<label v-for="db in sourceDatabases" :key="db.name" class="dbCheckItem" :class="{ dbCheckDisabled: db.fixed }">
							<input type="checkbox" v-model="db.checked" :disabled="anyActionInFlight || db.fixed" />
							<span class="dbCheckName">{{db.name}}</span>
							<span v-if="isWebAppDb(db.name)" class="dbWebAppTag" title="The web apps connect to this database.  Every web-app database must be selected for Migrate + cut over and Cut over only; Migrate only has no such requirement.">web app</span>
							<span v-if="db.state" class="dbState" :class="db.state === 'ONLINE' ? 'stateOk' : 'stateBad'">{{db.state}}</span>
							<span v-if="db.recoveryModel" class="mutedText">{{db.recoveryModel}}</span>
						</label>
					</div>
					<div v-else-if="!sourceDatabasesLoading && direction.source" class="mutedText">No databases to choose from.</div>
					<div class="formRow">
						<label class="formLabel" for="dbmCutoverTarget">Cutover target</label>
						<input id="dbmCutoverTarget" type="number" class="numberInput" min="0" max="3600" step="1" v-model.number="cutoverTargetSeconds" :disabled="anyActionInFlight" />
						<span class="mutedText">seconds — a target that only warns when exceeded; DbMigrate keeps shrinking the final log backup until it fits.</span>
					</div>
					<div class="formRow">
						<label class="advancedLabel" title="Advanced: keep the migration waiting at DbMigrate's operator gate (with catch-up rounds) until you click Approve cutover.  Otherwise the orchestrator approves automatically once its pre-cutover checks pass.">
							<input type="checkbox" v-model="holdAtGate" :disabled="anyActionInFlight" /> Hold at cutover gate <span class="mutedText">(advanced; default off)</span>
						</label>
					</div>
				</template>
			</div>

			<!-- ============================================================================================ -->
			<!-- Precondition checks                                                                           -->
			<!-- ============================================================================================ -->
			<div class="card" id="dbmPreconditions">
				<div class="secondaryHeading">Precondition checks</div>
				<p class="hintText">
					Read-only readiness checks for the direction configured above: DbMigrate's own Step-1 checks (disk space, backup shares,
					backup password, DbServerAgent readiness, …) plus the orchestrator's checks (both SQL Servers directly and from every web
					server, equal SQL Server versions, login/SID mapping, fleet agreement, free-space floor, runtime estimate).  A
					<strong>failure</strong> blocks the migration actions; a <strong>warning</strong> does not, but read it before proceeding.
				</p>
				<div class="formRow">
					<button type="button" class="gradientButton lightBlue" @click="runPreconditions" :disabled="!canRunPreconditions" :title="runPreconditionsReason || 'Run the checks now (read-only)'">Run precondition checks</button>
					<span v-if="preconditionsRunning" class="loadingInline"><ScaleLoader /> Running checks… this can take a minute.</span>
					<span v-else-if="runPreconditionsReason" class="disabledReason">{{runPreconditionsReason}}</span>
				</div>
				<div v-if="preconditionsError" class="errorBox">{{preconditionsError}}</div>
				<template v-if="preconditions">
					<div class="preHeader" :class="preconditions.anyFailed ? 'preHeaderFail' : (preconditions.anyWarned ? 'preHeaderWarn' : 'preHeaderPass')">
						<span class="stateBadge" :class="preconditions.anyFailed ? 'badgeBad' : (preconditions.anyWarned ? 'badgeWarn' : 'badgeGood')">{{preconditions.anyFailed ? 'FAILED' : (preconditions.anyWarned ? 'PASSED WITH WARNINGS' : 'PASSED')}}</span>
						<span class="preSummary">{{preconditions.summary}}</span>
						<span v-if="preconditions.dryRunStubbed" class="stateBadge badgeOff" title="Dry-run stub mode: synthetic results, no server was contacted.">stub</span>
					</div>
					<div class="mutedText preMeta">
						Checked {{formatUtc(preconditions.finishedUtc)}} for <code class="inline">{{preconditions.sourceServer}}</code> → <code class="inline">{{preconditions.targetServer}}</code><template v-if="preconditions.dbNames && preconditions.dbNames.length"> ({{preconditions.dbNames.join(', ')}})</template><template v-if="preconditions.totalMs"> in {{formatElapsed(preconditions.totalMs)}}</template>.
						<span v-if="preconditionsStale" class="preStale">The selection above has changed since — run the checks again before acting.</span>
					</div>
					<div v-if="preconditionWarnings.length" class="warningBox">
						<strong>{{preconditionWarnings.length}} warning{{preconditionWarnings.length === 1 ? '' : 's'}} — not blocking, but read before proceeding:</strong>
						<ul class="warningList">
							<li v-for="r in preconditionWarnings" :key="r.id">{{r.name}} — {{r.message}}</li>
						</ul>
					</div>
					<div v-for="g in preconditionGroups" :key="g.key" class="preGroup">
						<div class="preGroupTitle">
							<span>{{g.title}}</span>
							<span class="mutedText preGroupCounts">{{g.passed}} passed, {{g.warned}} warning{{g.warned === 1 ? '' : 's'}}, {{g.failed}} failed<template v-if="g.durationMs"> · <span class="preGroupTime" :title="g.durationHint">{{formatElapsed(g.durationMs)}}</span></template></span>
						</div>
						<div v-for="r in g.results" :key="r.id" class="preRow" :class="'pre' + r.state">
							<span class="preIcon" :title="r.state">{{preIcon(r.state)}}</span>
							<div class="preBody">
								<div class="preLine">
									<span class="preName">{{r.name}}</span>
									<span class="preMessage">{{r.message}}</span>
								</div>
								<div class="preFoot mutedText">
									<span v-if="r.durationMs" :title="r.group === 'DbMigrate' ? 'Time attributed to this result: from the previous result of the same check (or the check\'s start) to this one.  A result that shared a query with an earlier result of its check shows no time.' : 'Wall time of this check.'">{{formatMs(r.durationMs)}}</span>
									<button v-if="r.detail" type="button" class="linkButton" @click="toggleDetail(r.id)">{{expandedChecks[r.id] ? 'hide details' : 'details'}}</button>
								</div>
								<pre v-if="r.detail && expandedChecks[r.id]" class="preDetail">{{r.detail}}</pre>
							</div>
						</div>
					</div>
				</template>
				<div v-else-if="!preconditionsRunning" class="mutedText">No precondition checks have been run since TdsAppService started.</div>
			</div>

			<!-- ============================================================================================ -->
			<!-- Actions                                                                                       -->
			<!-- ============================================================================================ -->
			<div class="card" id="dbmActions">
				<div class="secondaryHeading">Actions</div>
				<p class="hintText">
					Every action runs behind the write permission and a confirmation dialog that lists exactly what will happen, in order, and
					what the expected impact is — nothing starts until you complete the slide control in that dialog.  The migration actions stay
					disabled until the precondition checks above pass for the current selection, while a run is in progress, or while the web
					servers disagree about their configuration — and say why.  The recovery tools need only the secrets and the write permission.
				</p>
				<div v-if="actionsNote" class="noteBox">{{actionsNote}}</div>
				<div class="actionGrid">
					<div v-for="a in actionDefs" :key="a.kind" class="actionItem" :class="{ actionReady: !actionDisabledReason(a.kind), actionPrimary: a.primary }">
						<div class="actionButtonRow">
							<button type="button" class="gradientButton" :class="a.primary ? 'tdsHealthBrown' : 'lightBlue'"
									:disabled="!!actionDisabledReason(a.kind) || anyActionInFlight"
									:title="actionDisabledReason(a.kind) || 'Opens a confirmation listing exactly what will happen, in order.'"
									@click="beginAction(a.kind)">{{a.label}}</button>
							<span v-if="actionBusy === a.kind" class="loadingInline"><ScaleLoader /> Preparing the preview…</span>
						</div>
						<div class="actionDesc">{{a.description}}</div>
						<div v-if="a.note" class="mutedText actionNote">{{a.note}}<template v-if="a.recovery && platform && platform.fleetAgrees === false">  The web servers currently disagree; the preview lists any blockers.</template></div>
						<div v-if="actionDisabledReason(a.kind)" class="disabledReason">{{actionDisabledReason(a.kind)}}</div>
						<div v-else class="readyReason">Ready — click to see the preview and confirm.</div>
					</div>
					<div class="actionItem actionPreview" :class="{ actionReady: !actionDisabledReason('Preview') }">
						<div class="actionButtonRow">
							<button type="button" class="gradientButton lightBlue"
									:disabled="!!actionDisabledReason('Preview') || anyActionInFlight"
									:title="actionDisabledReason('Preview') || 'Read-only: lists the exact steps, servers, files and target values without executing anything.'"
									@click="previewAction">Dry-run / preview</button>
							<select class="previewKindSelect" v-model="previewKind" :disabled="anyActionInFlight" title="Which kind of operation to preview">
								<option v-for="k in startableRunKinds" :key="k" :value="k">{{runKindLabel(k)}}</option>
							</select>
							<span v-if="actionBusy === 'Preview'" class="loadingInline"><ScaleLoader /> Building the preview…</span>
						</div>
						<div class="actionDesc">Lists the exact steps, servers, files and target values the selected operation would perform right now, without executing anything.  Read permission only; allowed even while a run is in progress.</div>
						<div v-if="actionDisabledReason('Preview')" class="disabledReason">{{actionDisabledReason('Preview')}}</div>
						<div v-else class="readyReason">Ready.</div>
					</div>
				</div>
			</div>

			<!-- ============================================================================================ -->
			<!-- History of previous runs                                                                      -->
			<!-- ============================================================================================ -->
			<div class="card" id="dbmHistory">
				<div class="secondaryHeading">Run history</div>
				<p class="hintText">
					Previous maintenance runs — manual, scheduled and recovery — with their outcomes and measured cutover durations.  Open a run to
					review its steps, DbMigrate detail and cutover plan in the operation panel; <em>Report</em> shows the run's HTML report.
				</p>
				<RunHistory :runs="history"
							:loading="historyLoading"
							:note="historyNote"
							:error="historyError"
							:selectedRunId="displayedRun ? displayedRun.runId : null"
							:disabled="anyActionInFlight"
							@select="selectHistoryRun"
							@report="showReport"
							@refresh="loadHistory" />
			</div>

			<!-- ============================================================================================ -->
			<!-- Secrets (write permission only)                                                               -->
			<!-- ============================================================================================ -->
			<div v-if="permissionWrite" class="card" id="dbmSecrets">
				<div class="secondaryHeading">Secrets</div>
				<p class="hintText">
					These credentials are stored only in TdsAppService's encrypted maintenance secrets file and are never displayed again.
					Leave a field blank to keep its stored value unchanged; tick <em>clear</em> to erase a stored value.
				</p>
				<div v-if="missingSecretNames.length" class="warningBox">
					Not yet configured: {{missingSecretNames.join(', ')}}.  Maintenance actions cannot run until every secret is set.
				</div>
				<div v-for="f in secretFields" :key="f.key" class="secretRow">
					<label class="formLabel secretLabel" :for="'secret_' + f.key">{{f.label}}</label>
					<input :id="'secret_' + f.key" type="password" autocomplete="new-password" spellcheck="false" class="secretInput"
						   v-model="secrets[f.key]" :disabled="secretsClear[f.key] || secretsSaving" :placeholder="secretsClear[f.key] ? 'will be cleared' : 'unchanged'" />
					<label class="clearLabel" :title="'Erase the stored ' + f.label + '.'"><input type="checkbox" v-model="secretsClear[f.key]" :disabled="secretsSaving" /> clear</label>
					<span class="secretState" :class="secretStateClass(f.key)">{{secretStateText(f.key)}}</span>
					<span v-if="isSecretMissing(f)" class="stateBadge badgeWarn" title="The server reports this secret is not set.">not set</span>
					<div class="secretHint mutedText">{{f.hint}}</div>
				</div>
				<div class="secretsCommitRow">
					<span v-if="secretsSaving" class="loadingInline"><ScaleLoader /> Saving…</span>
					<slide-button v-else
								  :key="'slide_secrets_' + slideCounter"
								  :auto-width="false"
								  :circle="true"
								  :noanimate="true"
								  :width="300"
								  :height="50"
								  text="slide to save secrets"
								  success-text="saving"
								  :disabled="!secretsHasChanges"
								  :requireReleaseToComplete="true"
								  @completed="saveSecrets" />
					<span v-if="!secretsHasChanges && !secretsSaving" class="disabledReason">Enter a new value or tick <em>clear</em> on at least one secret to enable saving.</span>
				</div>
				<div class="mutedText secretsMeta">{{secretsUpdatedText}}</div>
			</div>

			<!-- ============================================================================================ -->
			<!-- Configuration and schedule (read-only summary)                                                -->
			<!-- ============================================================================================ -->
			<div class="card" id="dbmConfig">
				<div class="secondaryHeading">Configuration &amp; schedule</div>
				<p class="hintText">Read-only summary of the maintenance settings.  Edit them on Admin → Settings.</p>
				<div v-if="configLoading && !config" class="loadingInline"><ScaleLoader /> Loading configuration…</div>
				<div v-else-if="configError" class="errorBox">{{configError}}</div>
				<template v-else-if="config">
					<div class="configColumns">
						<div class="configBlock">
							<div class="configTitle">Database servers</div>
							<div class="kvGrid">
								<span class="kvKey">Primary</span><span>{{endpointText(config.primary)}}</span>
								<span class="kvKey">Fallback</span><span>{{endpointText(config.fallback)}}</span>
								<span class="kvKey" title="dbMaintenanceDefaultDatabases: the databases the web apps connect to.  Selected by default, and every one of them must be selected for an operation that cuts the web apps over.">Web-app databases</span><span>{{config.defaultDatabases && config.defaultDatabases.length ? config.defaultDatabases.join(', ') : '—'}}</span>
								<span class="kvKey">Cutover target</span><span>{{config.cutoverTargetSeconds}} s (warn-only), up to {{config.cutoverMaxAttempts}} attempt{{config.cutoverMaxAttempts === 1 ? '' : 's'}}</span>
								<span class="kvKey">Free space floor</span><span>{{formatBytes(config.freeSpaceFloorBytes)}}</span>
								<span class="kvKey" title="Every web-app cutover sets the abandoned source databases READ_ONLY at the final snapshot, so no write is silently lost while the web apps hot-swap to the target; a rollback sets them READ_WRITE again.">Source read-only at cutover</span><span>always (hot-swap cutover)</span>
							</div>
						</div>
						<div class="configBlock">
							<div class="configTitle">Automated maintenance window</div>
							<div class="kvGrid">
								<span class="kvKey">Schedule</span>
								<span>
									<span class="stateBadge" :class="schedule.enabled ? 'badgeGood' : 'badgeOff'">{{schedule.enabled ? 'enabled' : 'disabled'}}</span>
									<template v-if="schedule.dayOfWeek || schedule.localTime"> {{schedule.dayOfWeek || '?'}} at {{schedule.localTime || '?'}}</template>
								</span>
								<span class="kvKey">Weeks of month</span>
								<span class="weekChips">
									<span v-for="w in [1, 2, 3, 4]" :key="w" class="weekChip" :class="weekOn(w) ? 'weekOn' : 'weekOff'">{{weekOn(w) ? '✓' : '✗'}} {{ordinal(w)}}</span>
									<span v-for="w in extraWeeks" :key="'x' + w" class="weekChip weekOn">✓ {{ordinal(w)}}</span>
								</span>
								<span class="kvKey">Next run</span><span>{{schedule.nextRunLocal || (schedule.nextRunUtc ? formatUtc(schedule.nextRunUtc) : '—')}}</span>
								<span class="kvKey">Window limit</span><span>{{config.windowMaxMinutes}} min</span>
								<span class="kvKey">Patch provider</span><span>{{schedule.patchProvider || '—'}}</span>
								<span class="kvKey">Manual hold timeout</span><span>{{config.manualHoldTimeoutMinutes}} min → {{config.holdTimeoutAction || '—'}}</span>
							</div>
						</div>
						<div class="configBlock">
							<div class="configTitle">Orchestrator</div>
							<div class="kvGrid">
								<span class="kvKey">Authorized machine</span><span>{{config.orchestratorMachine || '(none — every instance runs in dry-run mode)'}}</span>
								<span class="kvKey">Dry-run read-only ops</span><span>{{config.dryRunReadOnlyOps || '—'}}</span>
								<span class="kvKey">Dry-run notifications</span><span>{{config.dryRunNotifications || '—'}}</span>
								<span class="kvKey">Secrets</span><span>{{config.secretsConfigured ? 'all configured' : 'incomplete'}} — {{secretsUpdatedText}}</span>
							</div>
						</div>
					</div>
				</template>
			</div>
		</template>
	</div>
</template>

<script>
	import SlideButton from 'appRoot/vues/common/controls/SlideButton.vue';
	import RunProgressPanel from 'appRoot/vues/client/dbmaintenance/RunProgressPanel.vue';
	import RunHistory from 'appRoot/vues/client/dbmaintenance/RunHistory.vue';
	import RunConfirmPopup from 'appRoot/vues/client/dbmaintenance/RunConfirmPopup.vue';
	import LockOverridePopup from 'appRoot/vues/client/dbmaintenance/LockOverridePopup.vue';
	import RunReportPopup from 'appRoot/vues/client/dbmaintenance/RunReportPopup.vue';
	import { ModalDialog, ModalConfirmDialog, CountOpenDialogs } from 'appRoot/scripts/ModalDialog';
	import
		{
			GetDbMaintenanceStatus, GetDbMaintenanceConfig, SetDbMaintenanceSecrets, GetDbMaintenanceSourceDatabases, RunDbMaintenancePreconditions,
			PreviewDbMaintenanceRun, StartDbMaintenanceRun, ControlDbMaintenanceRun, GetDbMaintenanceRun, GetDbMaintenanceHistory
		} from 'appRoot/api/DbMaintenance';
	import
		{
			startableRunKinds, isActiveRunStatus, isTerminalRunStatus, isAttentionRunStatus, isNotAvailableYet,
			runKindLabel, runStatusLabel, runStatusBadgeClass, formatUtc, formatDateTime, formatElapsed, formatMs
		} from 'appRoot/vues/client/dbmaintenance/runFormat';

	// Background refresh pauses after this long without user interaction, so an unattended page does not keep probing the fleet.
	const autoRefreshIdleMs = 60000;
	// The active run is polled on this cadence (independent of the 10 s status refresh) so per-step progress is live.
	const runPollMs = 2000;
	// Status fields that change on every probe without meaning anything changed.  Ignored when deciding whether to re-apply status.
	const volatileStatusKeys = new Set(['generatedUtc', 'generationMs', 'fromCache', 'checkedUtc', 'sqlLatencyMs', 'elapsedMs', 'heartbeatUtc']);

	export default {
		components: { SlideButton, RunProgressPanel, RunHistory },
		data()
		{
			return {
				error: null,
				loading: false,
				status: null,
				config: null,
				configLoading: false,
				configError: null,
				lastRefreshedAt: 0, // Timestamp (ms) of the last successful status query.
				now: Date.now(), // Ticks every second so the "last checked N s ago" readout reacts to the passage of time.
				refreshTimer: null,
				clockTimer: null,
				silentRefreshBusy: false,
				autoRefreshPaused: false,
				slideCounter: 0, // Incremented to reset all slide buttons after any action or data change.
				webServersExpanded: false, // User toggle for listing individual servers while they are summarized in aggregate.
				secretsSaving: false,
				secrets: { redgateBackupPassword: '', backupEncryptionPassword: '', dbServerAgentSharedSecret: '' },
				secretsClear: { redgateBackupPassword: false, backupEncryptionPassword: false, dbServerAgentSharedSecret: false },
				secretFields: [
					{ key: 'redgateBackupPassword', label: 'Redgate backup login password', hint: 'Password of the redgate_backup SQL login used by DbMigrate and by the direct database checks on both servers.' },
					{ key: 'backupEncryptionPassword', label: 'Backup encryption password', hint: 'Password protecting the encrypted SQL Backup Pro (.sqb) backup files that DbMigrate creates and restores.' },
					{ key: 'dbServerAgentSharedSecret', label: 'DbServerAgent shared secret', hint: 'Authenticates TdsAppService to the DbServerAgent /LogBackup endpoints on both database servers.' }
				],
				// ---- Configure the migration ----
				direction: { source: null, target: null }, // Endpoint names.
				directionUserSet: false, // True once the administrator picked a direction; the defaults then stop following the fleet.
				sourceDatabases: [], // { name, state, recoveryModel, selectedByDefault, checked, fixed }
				sourceDatabasesLoading: false,
				sourceDatabasesServer: null,
				sourceDatabasesNote: null,
				cutoverTargetSeconds: null, // Initialized from the configuration once it loads.
				holdAtGate: false,
				// ---- Preconditions ----
				preconditionsRunning: false,
				preconditionsResult: null, // The run started from this page; status.lastPreconditions is the fallback after a reload.
				preconditionsError: null,
				expandedChecks: {}, // check id -> true while its detail is expanded
				// ---- Runs ----
				liveRun: null, // Latest GetRun snapshot of the active run (polled every 2 s while it runs), or the run just started/finished from here.
				selectedRun: null, // A run opened from the history (read-only view), or null to show the active run.
				selectedRunLoading: null, // runId being loaded from the history, or null.
				runPollTimer: null,
				runPollBusy: false,
				runNote: null, // Grey note when the run backend is not available in this build.
				acknowledgedRunIds: {}, // runId -> true once acknowledged from this page, so the banner clears before the status catches up.
				actionBusy: null, // Kind whose preview/start is in flight, 'Preview' for the read-only preview, or null.
				controlBusy: null, // ControlRun action in flight, or null.
				lockOverrideBusy: false,
				dialogsOpen: 0, // Modal dialogs opened by this page; background refresh pauses while any is open.
				actionsNote: null, // Grey note when the run backend is not available in this build.
				previewKind: 'MigrateAndCutover', // Kind previewed by the "Dry-run / preview" action.
				startableRunKinds: startableRunKinds,
				history: [],
				historyLoading: false,
				historyNote: null,
				historyError: null,
				actionDefs: [
					{ kind: 'MigrateAndCutover', label: 'Migrate + cut over', primary: true, recovery: false, description: 'Migrate the selected databases from the source to the target with DbMigrate, then repoint every web server at the target.  This is also what "Restore database traffic to the primary" does.', note: null },
					{ kind: 'MigrateOnly', label: 'Migrate only (no web-app cutover)', primary: false, recovery: false, description: 'Migrate the selected databases from the source to the target with DbMigrate and leave every web server where it is; the source databases stay ONLINE and READ_WRITE.  For databases the web apps do not connect to, or to stage a copy before a separate "Cut over only".', note: 'Needs passing precondition checks for the current selection; the web-app databases need not be selected.' },
					{ kind: 'CutoverOnly', label: 'Cut over only (no migration)', primary: false, recovery: true, description: 'Recovery tool: repoint the web servers at the target without migrating.  The target databases must be ONLINE and their data may be stale unless they were just migrated.', note: 'Recovery tool: needs the secrets and the write permission, not passing precondition checks.' },
					{ kind: 'RollbackLastCutover', label: 'Roll back the last web-app cutover', primary: false, recovery: true, description: 'Restore the exact previous AppSettings contents recorded in the last cutover plan on every web server and recycle, repointing the fleet at the previous server.  The direction above is ignored.', note: 'Recovery tool: needs the secrets and the write permission, not passing precondition checks.' },
					{ kind: 'ResumeMigration', label: 'Resume interrupted migration', primary: false, recovery: false, description: 'Continue an interrupted DbMigrate job from its persisted state.  Approval at the cutover gate is required again.', note: null },
					{ kind: 'TakeLocalBaseBackups', label: 'Take local base backups', primary: false, recovery: false, description: 'Take a full backup of each selected database on the target so it can restore the migrated databases from its own local files.  A full backup takes time; you may prefer to time it yourself.', note: 'Post-migration tool: needs the secrets and the write permission, not passing precondition checks.' }
				]
			};
		},
		computed:
		{
			permissionRead()
			{
				return this.$store.state.userData.OnlineServerMaintenanceRead || this.$store.state.userData.OnlineServerMaintenanceWrite;
			},
			permissionWrite()
			{
				return this.$store.state.userData.OnlineServerMaintenanceWrite;
			},
			feature()
			{
				return this.status ? this.status.feature : null;
			},
			platform()
			{
				return this.status ? this.status.platform : null;
			},
			webServers()
			{
				return this.platform && Array.isArray(this.platform.webServers) ? this.platform.webServers : [];
			},
			/// True while any user-initiated action is in flight; background refresh is skipped so it does not fight the action.
			anyActionInFlight()
			{
				return this.secretsSaving || this.preconditionsRunning || !!this.actionBusy || !!this.controlBusy || this.lockOverrideBusy
					|| this.dialogsOpen > 0 || !!this.selectedRunLoading;
			},
			// ---- Runs ----
			runLock()
			{
				return this.status ? this.status.runLock : null;
			},
			/// The run to treat as current: the fresher polled snapshot when it describes the run the status reports (or a run this page
			/// just started or watched finish before the status caught up), otherwise the status's activeRun.
			activeRun()
			{
				let sr = this.status ? this.status.activeRun : null;
				if (this.liveRun && (!sr || sr.runId === this.liveRun.runId))
					return this.liveRun;
				return sr;
			},
			/// What the operation panel shows: a run opened from the history, else the current run.
			displayedRun()
			{
				return this.selectedRun || this.activeRun;
			},
			runCardHeading()
			{
				if (this.selectedRun)
					return 'Operation from history';
				let run = this.activeRun;
				if (!run)
					return 'Operation in progress';
				return isActiveRunStatus(run.status) ? 'Operation in progress' : 'Last operation';
			},
			/// True while the single-flight lock is held or the current run is still active; new runs are refused meanwhile.
			runInProgress()
			{
				return !!(this.runLock && this.runLock.held) || !!(this.activeRun && isActiveRunStatus(this.activeRun.status));
			},
			/// Explains who holds the lock / what is running, for the disabled-action captions.
			runInProgressReason()
			{
				let lock = this.runLock;
				let run = this.activeRun;
				if (lock && lock.held)
				{
					let who = lock.ownerDisplayName || lock.actor || 'unknown';
					let what = lock.operation || (run ? runKindLabel(run.kind) : 'a maintenance operation');
					if (lock.stale)
						return 'A STALE run lock is held by ' + who + ' (' + what + ').  No new operation can start until the lock is overridden in the operation panel.';
					return 'An operation is already in progress: ' + what + ', held by ' + who + ' on ' + (lock.ownerMachine || '?') + '.  Wait for it to finish (or stop it in the operation panel).';
				}
				if (run && isActiveRunStatus(run.status))
					return 'An operation is already in progress: ' + runKindLabel(run.kind) + ' (' + runStatusLabel(run.status) + ').  Wait for it to finish.';
				return null;
			},
			/// The run shown in the top banner: the current run while it is NeedsAttention / Interrupted and not yet acknowledged from here.
			attentionRun()
			{
				let run = this.activeRun;
				if (!run || !isAttentionRunStatus(run.status) || this.acknowledgedRunIds[run.runId])
					return null;
				return run;
			},
			lockHeartbeatAge()
			{
				let lock = this.runLock;
				if (!lock || !lock.heartbeatUtc)
					return 'unknown';
				let d = new Date(lock.heartbeatUtc);
				return isNaN(d.getTime()) ? 'unknown' : formatElapsed(this.now - d.getTime());
			},
			/// The kind the verdict card's recommended action starts (server-provided; the primary action by default).
			recommendedKind()
			{
				return (this.platform && this.platform.recommendedActionKind) || 'MigrateAndCutover';
			},
			/// Formatted timestamp of the last successful status query, including the date once more than 12 hours old.
			lastRefreshed()
			{
				if (!this.lastRefreshedAt)
					return null;
				let d = new Date(this.lastRefreshedAt);
				return this.now - this.lastRefreshedAt > 12 * 60 * 60 * 1000 ? d.toLocaleString() : d.toLocaleTimeString();
			},
			lastCheckedAgo()
			{
				if (!this.lastRefreshedAt)
					return '';
				let s = Math.max(0, Math.round((this.now - this.lastRefreshedAt) / 1000));
				if (s < 120)
					return s + ' s ago';
				return Math.round(s / 60) + ' min ago';
			},
			dryRunReadOnlyOpsText()
			{
				let mode = this.feature ? this.feature.dryRunReadOnlyOps : null;
				if (mode === 'Stub')
					return 'stubbed with synthetic data (Stub mode) — the status below is NOT real';
				if (mode === 'Live')
					return 'run for real against the live servers (Live mode)';
				return mode ? String(mode) : 'in an unknown mode';
			},
			/// Notices about the feature itself (missing secrets, missing endpoints, orchestration not running, shutdown pending).
			featureNotices()
			{
				let f = this.feature;
				if (!f)
					return [];
				let list = [];
				if (!f.secretsConfigured)
				{
					let names = f.missingSecrets && f.missingSecrets.length ? f.missingSecrets.map(this.friendlySecretName).join(', ') : 'one or more secrets';
					list.push({
						cssClass: 'noticeWarn',
						text: 'Secrets missing: ' + names + '.  Maintenance actions cannot run until they are entered.',
						linkText: this.permissionWrite ? 'Enter secrets' : null,
						anchor: 'dbmSecrets'
					});
				}
				if (!f.endpointsConfigured)
					list.push({ cssClass: 'noticeBad', text: 'Database server endpoints are not configured' + (f.configError ? ': ' + f.configError : '.'), linkText: 'See configuration', anchor: 'dbmConfig' });
				else if (f.configError)
					list.push({ cssClass: 'noticeBad', text: 'Configuration error: ' + f.configError, linkText: 'See configuration', anchor: 'dbmConfig' });
				if (!f.orchestrationStarted)
					list.push({ cssClass: 'noticeWarn', text: 'Maintenance orchestration is not running on this instance' + (f.orchestrationDisabledReason ? ': ' + f.orchestrationDisabledReason : ' (the startup gate has not run yet).') });
				if (f.shutdownPending)
					list.push({ cssClass: 'noticeWarn', text: 'TdsAppService is shutting down; new maintenance runs are refused.' });
				return list;
			},
			verdict()
			{
				let v = this.platform ? this.platform.verdict : null;
				switch (v)
				{
					case 'Healthy':
						return { label: 'Healthy', cardClass: 'verdictGood', badgeClass: 'badgeGood', defaultDetail: 'Every web app is connected to the primary database server and every health check passed.' };
					case 'Degraded':
						return { label: 'Degraded', cardClass: 'verdictWarn', badgeClass: 'badgeWarn', defaultDetail: 'The platform is serving traffic, but at least one health check failed.' };
					case 'NotInDesiredState':
						return { label: 'Not in desired state', cardClass: 'verdictBad', badgeClass: 'badgeBad', defaultDetail: 'The web apps are not all connected to the primary database server.' };
					default:
						return { label: 'Unknown', cardClass: 'verdictUnknown', badgeClass: 'badgeOff', defaultDetail: 'The platform state could not be determined.' };
				}
			},
			activeDbServerText()
			{
				let p = this.platform;
				if (!p)
					return 'unknown';
				if (p.activeDbServer)
					return p.activeDbServer;
				return p.fleetAgrees === false ? 'web servers disagree' : 'unknown';
			},
			activeMismatch()
			{
				let p = this.platform;
				return !!p && (!p.activeDbServer || (p.desiredDbServer && p.activeDbServer !== p.desiredDbServer));
			},
			/// Summarize the web servers on one line only when they all agree and are all healthy.
			showAggregate()
			{
				let agg = this.platform ? this.platform.aggregate : null;
				if (!agg || !agg.allAgree || !this.webServers.length)
					return false;
				return this.webServers.every(ws => ws.connected && ws.healthy && !ws.agentLacksRpc && !this.isLatent(ws));
			},
			webServersListVisible()
			{
				return !this.showAggregate || this.webServersExpanded;
			},
			aggregateText()
			{
				let agg = this.platform.aggregate;
				if (!agg.effectiveTaylorServer && !agg.effectiveUmlsServer)
					return agg.summary || (agg.healthy + ' of ' + agg.total + ' web servers agree and are healthy');
				let servers = agg.effectiveTaylorServer === agg.effectiveUmlsServer
					? 'taylor+umls on ' + agg.effectiveTaylorServer
					: 'taylor on ' + (agg.effectiveTaylorServer || '?') + ', umls on ' + (agg.effectiveUmlsServer || '?');
				return agg.healthy + ' of ' + agg.total + ' web servers agree: ' + servers + (agg.releaseName ? ', release ' + agg.releaseName : '') + ', all healthy';
			},
			/// Explains why individual servers are being listed instead of the aggregate line.
			webServersProblemSummary()
			{
				let p = this.platform;
				let parts = [];
				if (p.fleetDisagreement)
					parts.push(p.fleetDisagreement);
				else if (p.aggregate && !p.aggregate.allAgree)
					parts.push('The web servers do not all report the same database configuration.');
				let disconnected = this.webServers.filter(ws => !ws.connected).length;
				let outdated = this.webServers.filter(ws => ws.connected && ws.agentLacksRpc).length;
				let unhealthy = this.webServers.filter(ws => ws.connected && !ws.agentLacksRpc && !ws.healthy).length;
				let latent = this.webServers.filter(ws => this.isLatent(ws)).length;
				if (disconnected)
					parts.push(disconnected + ' of ' + this.webServers.length + ' agents disconnected.');
				if (outdated)
					parts.push(outdated + ' agent' + (outdated === 1 ? ' needs' : 's need') + ' updating before their status can be read.');
				if (unhealthy)
					parts.push(unhealthy + ' of ' + this.webServers.length + ' web servers unhealthy.');
				if (latent)
					parts.push(latent + ' server' + (latent === 1 ? ' has' : 's have') + ' an AppSettings change that has not been recycled into effect.');
				if (!parts.length)
					parts.push('Listing every web server because their status could not be summarized.');
				return parts.join('  ');
			},
			/// Primary and fallback cards, with a placeholder when an endpoint is missing from the status.
			dbServers()
			{
				let p = this.platform || {};
				return [
					p.primary || { role: 'primary', missing: true },
					p.fallback || { role: 'fallback', missing: true }
				];
			},
			/// Names of secrets the server reports as not set (from GetConfig, falling back to GetStatus).
			missingSecretNames()
			{
				let src = this.config && Array.isArray(this.config.missingSecrets) ? this.config.missingSecrets
					: this.feature && Array.isArray(this.feature.missingSecrets) ? this.feature.missingSecrets : [];
				return src.map(this.friendlySecretName);
			},
			secretsHasChanges()
			{
				return this.secretFields.some(f => this.secretsClear[f.key] || this.secrets[f.key] !== '');
			},
			secretsUpdatedText()
			{
				if (!this.config)
					return '';
				if (!this.config.secretsUpdatedUtc)
					return 'Secrets have never been saved from this page.';
				return 'Last updated ' + this.formatUtc(this.config.secretsUpdatedUtc) + (this.config.secretsUpdatedBy ? ' by ' + this.config.secretsUpdatedBy : '') + '.';
			},
			/// Live schedule from the status payload when available, otherwise the configured schedule.
			schedule()
			{
				return (this.status && this.status.schedule) || (this.config && this.config.schedule) || {};
			},
			extraWeeks()
			{
				let weeks = Array.isArray(this.schedule.weeksOfMonth) ? this.schedule.weeksOfMonth : [];
				return weeks.filter(w => w < 1 || w > 4);
			},
			// ---- Configure the migration ----
			/// The configured endpoint names (primary first), from the configuration or, before it loads, the status.
			endpointNames()
			{
				let names = [];
				if (this.config)
					names = [this.config.primary && this.config.primary.name, this.config.fallback && this.config.fallback.name];
				else if (this.platform)
					names = [this.platform.primary && this.platform.primary.name, this.platform.fallback && this.platform.fallback.name];
				return names.filter(n => !!n);
			},
			selectedDbNames()
			{
				return this.sourceDatabases.filter(d => d.checked).map(d => d.name);
			},
			/// The databases the web apps connect to (dbMaintenanceDefaultDatabases): a web-app cutover requires all of them to be selected.
			webAppDbNames()
			{
				return this.config && Array.isArray(this.config.defaultDatabases) ? this.config.defaultDatabases.filter(n => !!n) : [];
			},
			/// Web-app databases missing from the current selection (case-insensitive), in the configured order.
			missingWebAppDbNames()
			{
				let have = this.selectedDbNames.map(n => String(n).toLowerCase());
				return this.webAppDbNames.filter(n => have.indexOf(String(n).toLowerCase()) === -1);
			},
			/// Explains a missing or suspicious default direction.
			directionNote()
			{
				if (!this.endpointNames.length)
					return null;
				let p = this.platform;
				if (p && p.fleetAgrees === false && !this.direction.source)
					return 'No default direction because the web servers disagree about which database server they are connected to'
						+ (p.fleetDisagreement ? ': ' + p.fleetDisagreement : '.') + '  Choose the source deliberately: it must be the server the web apps are actually using.';
				if (this.direction.source && this.direction.target && this.direction.source === this.direction.target)
					return 'Source and target must be different servers.';
				if (p && p.activeDbServer && this.direction.source && this.direction.source !== p.activeDbServer)
					return 'The web apps are currently connected to ' + p.activeDbServer + ', but the source is ' + this.direction.source
						+ '.  Migrating in this direction would copy idle databases over the live ones; the precondition checks will fail unless the fleet moves first.';
				return null;
			},
			// ---- Preconditions ----
			/// The result to display: the run started from this page, else the server's last run (survives a page reload).
			preconditions()
			{
				return this.preconditionsResult || (this.status && this.status.lastPreconditions) || null;
			},
			canRunPreconditions()
			{
				return !this.preconditionsRunning && !this.secretsSaving && !!this.direction.source && !!this.direction.target
					&& this.direction.source !== this.direction.target && this.selectedDbNames.length > 0 && !this.sourceDatabasesLoading;
			},
			runPreconditionsReason()
			{
				if (this.preconditionsRunning)
					return null;
				if (!this.endpointNames.length)
					return 'The database server endpoints are not configured.';
				if (!this.direction.source || !this.direction.target)
					return 'Choose a source and a target server first.';
				if (this.direction.source === this.direction.target)
					return 'Source and target must be different servers.';
				if (this.sourceDatabasesLoading)
					return 'Waiting for the database list…';
				if (!this.selectedDbNames.length)
					return 'Select at least one database.';
				return null;
			},
			/// True when the displayed result was produced for a different direction or database selection than the current one.
			preconditionsStale()
			{
				let p = this.preconditions;
				if (!p)
					return false;
				let eq = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();
				let norm = arr => (Array.isArray(arr) ? arr : []).map(s => String(s).toLowerCase()).sort().join(',');
				return !eq(p.sourceServer, this.direction.source) || !eq(p.targetServer, this.direction.target) || norm(p.dbNames) !== norm(this.selectedDbNames);
			},
			preconditionWarnings()
			{
				let p = this.preconditions;
				return p && Array.isArray(p.results) ? p.results.filter(r => r.state === 'warn') : [];
			},
			/// Results grouped DbMigrate / Orchestrator (any unexpected group last), with per-group counts.
			preconditionGroups()
			{
				let p = this.preconditions;
				let results = p && Array.isArray(p.results) ? p.results : [];
				// Both groups run concurrently, so each heading shows its own wall time and the header's total is about the longer of the two.
				let concurrent = 'Wall time of this group\'s checks.  The DbMigrate and Orchestrator groups run at the same time, so the whole run takes about as long as the slower group.';
				let defs = [
					{ key: 'DbMigrate', title: 'DbMigrate checks (Step 1 of the migration)', durationMs: p ? p.dbMigrateSectionMs : 0, durationHint: concurrent + '  Within this group, independent checks also run at the same time; each result shows the time attributed to it.' },
					{ key: 'Orchestrator', title: 'Orchestrator checks', durationMs: p ? p.orchestratorSectionMs : 0, durationHint: concurrent }
				];
				for (let i = 0; i < results.length; i++)
					if (!defs.some(d => d.key === results[i].group))
						defs.push({ key: results[i].group || 'Other', title: results[i].group || 'Other checks', durationMs: 0, durationHint: '' });
				return defs.map(d =>
				{
					let list = results.filter(r => (r.group || 'Other') === d.key);
					return {
						key: d.key,
						title: d.title,
						durationMs: d.durationMs,
						durationHint: d.durationHint,
						results: list,
						passed: list.filter(r => r.state === 'pass').length,
						warned: list.filter(r => r.state === 'warn').length,
						failed: list.filter(r => r.state === 'fail').length
					};
				}).filter(g => g.results.length > 0);
			}
		},
		created()
		{
			if (this.permissionRead)
			{
				this.loadStatus(false);
				this.loadConfig();
				// Fleet and database state change over time, so refresh silently in the background.
				this.loadHistory();
				this.refreshTimer = setInterval(this.silentRefresh, 10000);
				this.clockTimer = setInterval(() => { this.now = Date.now(); }, 1000);
				this.runPollTimer = setInterval(this.pollRun, runPollMs);
				this._lastActiveRunKey = null; // "runId|status" of the last status-reported active run, to detect run changes (non-reactive).
				// _lastInteraction is deliberately a non-reactive field because mouse movement updates it constantly.
				this._lastInteraction = Date.now();
				this._dbLoadToken = 0; // Guards against out-of-order database list responses.
				this._dbLoadKey = null; // "source|secretsConfigured" of the last database list load.
				this._interactionEvents = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];
				for (let i = 0; i < this._interactionEvents.length; i++)
					document.addEventListener(this._interactionEvents[i], this.onUserInteraction, { passive: true, capture: true });
			}
		},
		beforeUnmount()
		{
			clearInterval(this.refreshTimer);
			clearInterval(this.clockTimer);
			clearInterval(this.runPollTimer);
			if (this._interactionEvents)
				for (let i = 0; i < this._interactionEvents.length; i++)
					document.removeEventListener(this._interactionEvents[i], this.onUserInteraction, { capture: true });
		},
		methods:
		{
			/// True only when a web server actually reported both its configured and effective database servers and they differ
			/// ("written but not recycled").  A disconnected or unreadable server has configMatchesEffective === false too, but
			/// that is absence of data, not a latent cutover.
			isLatent(ws)
			{
				return !!ws && ws.connected && !ws.agentLacksRpc && (ws.kentResponded || ws.adminResponded)
					&& !!(ws.effectiveTaylorServer || ws.effectiveUmlsServer) && ws.configMatchesEffective === false;
			},
			/// True when the web apps connect to this database (see webAppDbNames).
			isWebAppDb(name)
			{
				let n = String(name || '').toLowerCase();
				return this.webAppDbNames.some(w => String(w).toLowerCase() === n);
			},
			/// Manual Refresh button: bypasses the server-side status cache and re-reads the configuration.
			refreshAll()
			{
				this.loadStatus(true);
				this.loadConfig();
			},
			loadStatus(forceRefresh)
			{
				this.loading = true;
				this.error = null;
				GetDbMaintenanceStatus(forceRefresh === true)
					.then(result =>
					{
						if (result.error)
						{
							this.error = result.error;
							return;
						}
						this.applyStatus(result);
					})
					.catch(ex =>
					{
						this.error = ex.message || String(ex);
					})
					.finally(() =>
					{
						this.loading = false;
					});
			},
			loadConfig()
			{
				this.configLoading = true;
				GetDbMaintenanceConfig()
					.then(result =>
					{
						if (result.error)
						{
							this.configError = result.error;
							return;
						}
						this.configError = null;
						this.config = result;
						this.ensureDirectionDefaults();
					})
					.catch(ex =>
					{
						this.configError = ex.message || String(ex);
					})
					.finally(() =>
					{
						this.configLoading = false;
					});
			},
			onUserInteraction()
			{
				this._lastInteraction = Date.now();
				if (this.autoRefreshPaused)
				{
					this.autoRefreshPaused = false;
					this.silentRefresh(); // Catch up promptly after an idle period.
				}
			},
			silentRefresh()
			{
				this.now = Date.now();
				if (document.hidden)
					return;
				if (Date.now() - this._lastInteraction > autoRefreshIdleMs)
				{
					this.autoRefreshPaused = true;
					return;
				}
				// Any open modal dialog (ours, or e.g. the "reload to update" prompt) pauses the refresh so it never fights the administrator.
				if (this.loading || this.silentRefreshBusy || this.anyActionInFlight || CountOpenDialogs() > 0)
					return;
				this.silentRefreshBusy = true;
				GetDbMaintenanceStatus(false)
					.then(result =>
					{
						if (result.error)
							return;
						// Only re-apply (and reset slide buttons) when something meaningful changed, so re-rendering does not disturb an in-progress slide gesture.
						// The active run is deliberately not part of that comparison: its snapshot changes every few seconds while a run is in
						// flight, and the run panel keys its own controls, so run-only changes are applied without resetting the page's slide buttons.
						if (!this.status || this.significantStatusJson(result) !== this.significantStatusJson(this.status))
						{
							this.error = null;
							this.applyStatus(result, true);
						}
						else
						{
							// Nothing meaningful changed outside the run; still pick up the volatile fields (check times, latency, cache flag) and the run.
							this.applyStatus(result, false);
						}
					})
					.catch(() => { })
					.finally(() => { this.silentRefreshBusy = false; });
			},
			/// JSON of the status payload with the always-changing fields (and the active run) removed, for change detection.
			significantStatusJson(s)
			{
				return JSON.stringify({ feature: s.feature, platform: s.platform, runLock: s.runLock, schedule: s.schedule },
					(key, value) => volatileStatusKeys.has(key) ? undefined : value);
			},
			applyStatus(status, resetSlides)
			{
				this.status = status;
				this.lastRefreshedAt = Date.now();
				this.now = this.lastRefreshedAt;
				if (resetSlides !== false)
					this.slideCounter++;
				this.ensureDirectionDefaults();
				this.onStatusRunChanged(status.activeRun);
			},
			/// Reconciles the status-reported active run with the polled snapshot, and reloads the history when the run changes.
			onStatusRunChanged(statusRun)
			{
				if (this.liveRun && statusRun)
				{
					if (statusRun.runId !== this.liveRun.runId)
						this.liveRun = null; // A different run (e.g. started by the scheduler) replaces the one being shown.
					else if ((statusRun.elapsedMs || 0) >= (this.liveRun.elapsedMs || 0))
						this.liveRun = statusRun; // Same run and the status is at least as fresh (elapsedMs only grows).
				}
				let key = statusRun ? statusRun.runId + '|' + statusRun.status : '';
				if (this._lastActiveRunKey !== null && key !== this._lastActiveRunKey)
					this.loadHistory();
				this._lastActiveRunKey = key;
			},
			scrollTo(elementId)
			{
				let el = document.getElementById(elementId);
				if (el)
					el.scrollIntoView({ behavior: 'smooth', block: 'start' });
			},
			// ---- Configure the migration ---------------------------------------------------------------------------
			endpointOptionLabel(name)
			{
				let role = this.config && this.config.primary && this.config.primary.name === name ? 'primary'
					: this.config && this.config.fallback && this.config.fallback.name === name ? 'fallback'
					: this.platform && this.platform.primary && this.platform.primary.name === name ? 'primary'
					: this.platform && this.platform.fallback && this.platform.fallback.name === name ? 'fallback' : null;
				let active = this.platform && this.platform.activeDbServer === name;
				return name + (role ? ' (' + role + (active ? ', active' : '') + ')' : (active ? ' (active)' : ''));
			},
			/// Applies the default direction (source = the active database server, target = the other one) while the administrator
			/// has not chosen one, initializes the cutover target from the configuration, and (re)loads the database list when needed.
			ensureDirectionDefaults()
			{
				if (this.config && this.cutoverTargetSeconds === null && typeof this.config.cutoverTargetSeconds === 'number')
					this.cutoverTargetSeconds = this.config.cutoverTargetSeconds;
				let names = this.endpointNames;
				if (!names.length)
					return;
				if (!this.directionUserSet)
				{
					let p = this.platform;
					let source = null;
					if (p && p.activeDbServer && names.indexOf(p.activeDbServer) !== -1)
						source = p.activeDbServer;
					else if (p && p.fleetAgrees === false)
						source = null; // No default while the web servers disagree (the note explains).
					else if (p && p.desiredDbServer && names.indexOf(p.desiredDbServer) !== -1)
						source = p.desiredDbServer;
					else if (p)
						source = names[0];
					let target = source ? (names.find(n => n !== source) || null) : null;
					if (this.direction.source !== source || this.direction.target !== target)
						this.direction = { source, target };
				}
				this.maybeLoadSourceDatabases();
			},
			onDirectionChanged(which)
			{
				this.directionUserSet = true;
				let names = this.endpointNames;
				if (this.direction.source && this.direction.source === this.direction.target)
				{
					let other = names.find(n => n !== this.direction.source) || null;
					if (which === 'source')
						this.direction.target = other;
					else
						this.direction.source = other;
				}
				this.maybeLoadSourceDatabases();
			},
			swapDirection()
			{
				if (!this.direction.source || !this.direction.target)
					return;
				this.directionUserSet = true;
				this.direction = { source: this.direction.target, target: this.direction.source };
				this.maybeLoadSourceDatabases();
			},
			/// Loads the database list only when the source or the secrets state changed since the last load.
			maybeLoadSourceDatabases()
			{
				let secretsOk = this.config ? !!this.config.secretsConfigured : (this.feature ? !!this.feature.secretsConfigured : false);
				let key = (this.direction.source || '') + '|' + (secretsOk ? 1 : 0) + '|' + (this.config ? 1 : 0);
				if (key === this._dbLoadKey)
					return;
				this._dbLoadKey = key;
				this.loadSourceDatabases(secretsOk);
			},
			loadSourceDatabases(secretsOk)
			{
				let source = this.direction.source;
				let token = ++this._dbLoadToken;
				if (!source)
				{
					this.sourceDatabases = [];
					this.sourceDatabasesServer = null;
					this.sourceDatabasesNote = null;
					this.sourceDatabasesLoading = false;
					return;
				}
				let defaults = this.config && Array.isArray(this.config.defaultDatabases) ? this.config.defaultDatabases : [];
				if (!secretsOk)
				{
					this.applyFallbackDatabases(source, defaults, 'The secrets are not configured, so the databases on ' + source + ' cannot be listed; the configured default databases are used.');
					return;
				}
				this.sourceDatabasesLoading = true;
				GetDbMaintenanceSourceDatabases(source)
					.then(result =>
					{
						if (token !== this._dbLoadToken)
							return;
						if (result.error)
						{
							this.applyFallbackDatabases(source, defaults, 'Could not list the databases on ' + source + ' (' + result.error + '); the configured default databases are used.');
							return;
						}
						this.sourceDatabasesServer = result.sourceServer || source;
						this.sourceDatabasesNote = null;
						this.sourceDatabases = (Array.isArray(result.databases) ? result.databases : []).map(d => ({
							name: d.name,
							state: d.state,
							recoveryModel: d.recoveryModel,
							selectedByDefault: !!d.selectedByDefault,
							checked: !!d.selectedByDefault,
							fixed: false
						}));
					})
					.catch(ex =>
					{
						if (token === this._dbLoadToken)
							this.applyFallbackDatabases(source, defaults, 'Could not list the databases on ' + source + ' (' + (ex.message || String(ex)) + '); the configured default databases are used.');
					})
					.finally(() =>
					{
						if (token === this._dbLoadToken)
							this.sourceDatabasesLoading = false;
					});
			},
			/// Shows the configured default databases as fixed, checked entries when the live list is unavailable.
			applyFallbackDatabases(source, defaults, note)
			{
				this.sourceDatabasesServer = source;
				this.sourceDatabasesNote = note;
				this.sourceDatabases = defaults.map(name => ({ name, state: null, recoveryModel: null, selectedByDefault: true, checked: true, fixed: true }));
				this.sourceDatabasesLoading = false;
			},
			// ---- Preconditions ------------------------------------------------------------------------------------
			runPreconditions()
			{
				if (!this.canRunPreconditions)
					return;
				this.preconditionsRunning = true;
				this.preconditionsError = null;
				RunDbMaintenancePreconditions(this.direction.source, this.direction.target, this.selectedDbNames.slice())
					.then(result =>
					{
						if (result.error)
						{
							this.preconditionsError = result.error;
							toaster.Error(result.error);
							return;
						}
						this.preconditionsResult = result;
						this.expandedChecks = {};
					})
					.catch(ex =>
					{
						this.preconditionsError = ex.message || String(ex);
					})
					.finally(() =>
					{
						this.preconditionsRunning = false;
					});
			},
			toggleDetail(id)
			{
				this.expandedChecks[id] = !this.expandedChecks[id];
			},
			preIcon(state)
			{
				switch (state)
				{
					case 'pass': return '✓';
					case 'warn': return '!';
					case 'fail': return '✗';
					default: return '?';
				}
			},
			// ---- Actions -------------------------------------------------------------------------------------------
			/// Why an action is disabled right now, or null when it may start.  Every rule states the reason so the administrator knows
			/// what to fix (spec: explain, never just grey out).  `kind` is a DbMaintenanceRunKind, or 'Preview' for the read-only
			/// preview of this.previewKind.
			actionDisabledReason(kind)
			{
				let f = this.feature;
				let isPreview = kind === 'Preview';
				let effectiveKind = isPreview ? this.previewKind : kind;
				if (!isPreview && !this.permissionWrite)
					return 'You do not have the Online Server Maintenance Write permission.';
				if (!this.endpointNames.length || (f && !f.endpointsConfigured))
					return 'The database server endpoints are not configured (see Configuration below).';
				if (!isPreview)
				{
					if (f && f.shutdownPending)
						return 'TdsAppService is shutting down; new maintenance runs are refused.';
					if (f && !f.orchestrationStarted)
						return 'Maintenance orchestration is not running on this instance' + (f.orchestrationDisabledReason ? ': ' + f.orchestrationDisabledReason : ' (the startup gate has not run yet).');
					if (f && !f.secretsConfigured)
						return 'Secrets missing: ' + (this.missingSecretNames.length ? this.missingSecretNames.join(', ') : 'one or more secrets') + '.  Enter them in Secrets below.';
					if (this.runInProgressReason)
						return this.runInProgressReason;
				}
				// A rollback repoints the fleet from the persisted plan; every other kind acts on the configured direction and databases.
				if (effectiveKind !== 'RollbackLastCutover')
				{
					if (!this.direction.source || !this.direction.target)
						return 'Choose a source and a target server first.';
					if (this.direction.source === this.direction.target)
						return 'Source and target must be different servers.';
					if (this.sourceDatabasesLoading)
						return 'Waiting for the database list…';
					if (!this.selectedDbNames.length)
						return 'Select at least one database.';
				}
				if (isPreview)
					return null;
				// The kinds that repoint the web apps need every web-app database in the selection (the server enforces the same rule; a
				// resume takes its selection from the persisted job, so the server alone decides there).
				if (kind === 'MigrateAndCutover' || kind === 'CutoverOnly')
				{
					let missing = this.missingWebAppDbNames;
					if (missing.length)
						return 'Disabled: the web apps cannot be cut over unless every web-app database (' + this.webAppDbNames.join(', ') + ') is selected; missing: ' + missing.join(', ') + '.'
							+ (kind === 'MigrateAndCutover' ? '  Select them, or use "Migrate only (no web-app cutover)".' : '  Select them first.');
				}
				// The kinds that migrate data require passing precondition checks for the current selection, and those that also cut over
				// require fleet agreement.  Cut over only, roll back and take local base backups are recovery / post-migration tools and
				// deliberately need neither.
				if (kind === 'MigrateAndCutover' || kind === 'MigrateOnly' || kind === 'ResumeMigration')
				{
					if (kind !== 'MigrateOnly' && this.platform && this.platform.fleetAgrees === false)
						return 'Disabled: the web servers disagree about their database configuration (see the web servers above).  Converge them first with "Cut over only" or "Roll back".';
					let p = this.preconditions;
					if (!p)
						return 'Run the precondition checks first.';
					if (this.preconditionsStale)
						return 'Run the precondition checks again for the current source, target and database selection.';
					if (p.anyFailed)
					{
						let first = Array.isArray(p.results) ? p.results.find(r => r.state === 'fail') : null;
						return 'Precondition checks failed: ' + (first ? first.name + ' — ' + first.message : p.summary);
					}
				}
				return null;
			},
			/// The DbMaintenanceRunRequest for a kind, from the "Configure the migration" section.
			buildRunRequest(kind)
			{
				let target = typeof this.cutoverTargetSeconds === 'number' && this.cutoverTargetSeconds >= 0 ? this.cutoverTargetSeconds
					: (this.config && typeof this.config.cutoverTargetSeconds === 'number' ? this.config.cutoverTargetSeconds : 15);
				return {
					kind,
					sourceServerName: this.direction.source,
					targetServerName: this.direction.target,
					dbNames: this.selectedDbNames.slice(),
					targetDbNames: null,
					cutoverTargetSeconds: target,
					holdAtGate: !!this.holdAtGate,
					confirmed: false
				};
			},
			/// Opens a modal dialog through the shared helper while counting it as in flight, so background refresh pauses until it closes.
			openDialog(factory)
			{
				this.dialogsOpen++;
				return factory().finally(() => { this.dialogsOpen--; });
			},
			/// A stub "Not available yet" reply from the run features becomes a grey note instead of a red error.
			noteNotAvailable(error)
			{
				this.actionsNote = 'The migration and cutover backend is not available in this build yet (server: ' + error + ').  The status, precondition checks and secrets above work; the actions and history light up when the orchestrator ships.';
			},
			/// Preview → confirmation dialog (slide to start) → StartRun, for one of the startable kinds.
			beginAction(kind)
			{
				if (this.actionDisabledReason(kind) || this.anyActionInFlight)
					return;
				let request = this.buildRunRequest(kind);
				this.actionBusy = kind;
				PreviewDbMaintenanceRun(request)
					.then(result =>
					{
						if (result.error)
						{
							if (isNotAvailableYet(result.error))
								this.noteNotAvailable(result.error);
							else
								toaster.Error(result.error);
							return;
						}
						this.actionsNote = null;
						let props = { kind, preview: result.preview || null, request, readOnly: false, dryRun: !!(this.feature && this.feature.dryRun) };
						return this.openDialog(() => ModalDialog(RunConfirmPopup, props, { defaultCloseOnOverlayClick: false }))
							.then(confirmed =>
							{
								if (!confirmed)
									return;
								request.confirmed = true;
								return StartDbMaintenanceRun(request).then(started =>
								{
									if (started.error)
									{
										toaster.Error(started.error);
										return;
									}
									toaster.Success(started.message || (runKindLabel(kind) + ' started.'));
									this.selectedRun = null;
									this.runNote = null;
									if (started.run)
										this.liveRun = started.run;
									else if (started.runId) // Minimal placeholder so polling begins immediately; the first GetRun replaces it.
										this.liveRun = { runId: started.runId, kind, status: 'Running', origin: 'Manual', startedUtc: new Date().toISOString(), elapsedMs: 0, steps: [] };
									this.loadStatus(false);
									this.loadHistory();
									this.scrollTo('dbmRun');
								});
							});
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					})
					.finally(() =>
					{
						this.actionBusy = null;
						this.slideCounter++;
					});
			},
			/// Read-only dry-run / preview of the selected kind: the same dialog with no start control.
			previewAction()
			{
				if (this.actionDisabledReason('Preview') || this.anyActionInFlight)
					return;
				let kind = this.previewKind;
				let request = this.buildRunRequest(kind);
				this.actionBusy = 'Preview';
				PreviewDbMaintenanceRun(request)
					.then(result =>
					{
						if (result.error)
						{
							if (isNotAvailableYet(result.error))
								this.noteNotAvailable(result.error);
							else
								toaster.Error(result.error);
							return;
						}
						this.actionsNote = null;
						let props = { kind, preview: result.preview || null, request, readOnly: true, dryRun: !!(this.feature && this.feature.dryRun) };
						return this.openDialog(() => ModalDialog(RunConfirmPopup, props));
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					})
					.finally(() =>
					{
						this.actionBusy = null;
					});
			},
			/// Sends a control action (Stop | ApproveCutover | ResumeAfterHold | EndWindowAfterHold | Acknowledge) for the current run.
			/// Stop and EndWindowAfterHold ask for a second confirmation because they end work; the slide control is the confirmation for the rest.
			controlRun(action)
			{
				let run = this.activeRun;
				if (!run || !run.runId || !this.permissionWrite || this.controlBusy)
					return;
				let confirm = Promise.resolve(true);
				if (action === 'Stop')
					confirm = this.openDialog(() => ModalConfirmDialog(
						'Stop "' + runKindLabel(run.kind) + '" now?\n\nA DbMigrate job cancels cleanly: the source database stays live and untouched; target databases may be left RESTORING, which a later run can resume over.  A web-app cutover step already sent to the agents completes on its own.',
						'Stop the operation', 'Stop', 'Keep running'));
				else if (action === 'EndWindowAfterHold')
					confirm = this.openDialog(() => ModalConfirmDialog(
						'End the maintenance window WITHOUT the restore leg?\n\nThe web apps stay connected to the fallback database server until someone restores traffic to the primary from this page.  The platform is stable in that state, just not in its desired configuration.',
						'End the window', 'End the window', 'Cancel'));
				confirm.then(ok =>
				{
					if (!ok)
					{
						this.slideCounter++;
						return;
					}
					this.controlBusy = action;
					return ControlDbMaintenanceRun(run.runId, action)
						.then(result =>
						{
							if (result.error)
							{
								toaster.Error(result.error);
								return;
							}
							toaster.Success(result.message || (action + ' accepted.'));
							if (action === 'Acknowledge')
								this.acknowledgedRunIds[run.runId] = true;
							if (result.run)
								this.applyLiveRun(result.run);
							this.loadStatus(false);
							this.loadHistory();
						})
						.catch(ex =>
						{
							toaster.Error(ex.message || String(ex));
						})
						.finally(() =>
						{
							this.controlBusy = null;
							this.slideCounter++;
						});
				});
			},
			/// Stale-lock override: the administrator must type the lock owner's display name; the server logs the override.
			overrideLock()
			{
				let lock = this.runLock;
				if (!lock || !lock.held || !lock.stale || !this.permissionWrite || this.anyActionInFlight)
					return;
				this.openDialog(() => ModalDialog(LockOverridePopup, { lock }, { defaultCloseOnOverlayClick: false }))
					.then(result =>
					{
						if (!result || !result.ownerName)
							return;
						this.lockOverrideBusy = true;
						return ControlDbMaintenanceRun(lock.runId || null, 'OverrideLock', result.ownerName)
							.then(r =>
							{
								if (r.error)
									toaster.Error(r.error);
								else
									toaster.Success(r.message || 'The stale lock was overridden.');
							})
							.catch(ex =>
							{
								toaster.Error(ex.message || String(ex));
							})
							.finally(() =>
							{
								this.lockOverrideBusy = false;
								this.loadStatus(true);
								this.loadHistory();
							});
					});
			},
			// ---- Run polling and history ---------------------------------------------------------------------------
			/// Polls GetRun for the current run every 2 s while its status is active (independent of the 10 s status refresh), pausing
			/// while the tab is hidden or a control is being sent, and stopping by itself once the status is terminal.
			pollRun()
			{
				if (document.hidden || this.runPollBusy || this.controlBusy)
					return;
				let run = this.activeRun;
				if (!run || !run.runId || !isActiveRunStatus(run.status))
					return;
				this.runPollBusy = true;
				GetDbMaintenanceRun(run.runId)
					.then(result =>
					{
						if (result.error)
						{
							if (isNotAvailableYet(result.error))
								this.runNote = 'Live run progress is not available in this build yet (server: ' + result.error + ').';
							else
								this.onRunPollFailure(run, result.error);
							return;
						}
						this._runPollFailures = 0;
						this.runNote = null;
						if (result.run)
							this.applyLiveRun(result.run);
					})
					.catch(ex => { this.onRunPollFailure(run, ex.message || String(ex)); })
					.finally(() => { this.runPollBusy = false; });
			},
			/// A run this page is polling but the server no longer reports (e.g. it vanished with a restart) must not be polled forever:
			/// after several consecutive failures with no matching active run in the status, drop the local snapshot and say so.
			onRunPollFailure(run, error)
			{
				this._runPollFailures = (this._runPollFailures || 0) + 1;
				let statusRun = this.status ? this.status.activeRun : null;
				if (this._runPollFailures >= 5 && this.liveRun && this.liveRun.runId === run.runId && (!statusRun || statusRun.runId !== run.runId))
				{
					this._runPollFailures = 0;
					this.liveRun = null;
					this.runNote = 'Lost track of run ' + run.runId + ' (' + error + ').  The status above and the history below are authoritative.';
					this.loadHistory();
				}
			},
			/// Applies a fresh run snapshot; a transition to a terminal status refreshes the status and history and announces the outcome.
			applyLiveRun(run)
			{
				let prev = this.activeRun;
				this.liveRun = run;
				if (prev && prev.runId === run.runId && prev.status !== run.status && isTerminalRunStatus(run.status))
				{
					let text = runKindLabel(run.kind) + ': ' + runStatusLabel(run.status);
					if (run.status === 'Succeeded' || run.status === 'SucceededWithWarnings')
						toaster.Success(text);
					else if (run.status === 'Cancelled' || run.status === 'Suspended')
						toaster.Warning(text);
					else
						toaster.Error(text, run.error || undefined);
					this.loadStatus(false);
					this.loadHistory();
				}
			},
			loadHistory()
			{
				if (this.historyLoading)
					return;
				this.historyLoading = true;
				GetDbMaintenanceHistory(50)
					.then(result =>
					{
						if (result.error)
						{
							if (isNotAvailableYet(result.error))
							{
								this.history = [];
								this.historyNote = 'Run history is not available in this build yet (server: ' + result.error + ').';
								this.historyError = null;
							}
							else
								this.historyError = result.error;
							return;
						}
						this.history = Array.isArray(result.runs) ? result.runs : [];
						this.historyNote = null;
						this.historyError = null;
					})
					.catch(ex =>
					{
						this.historyError = ex.message || String(ex);
					})
					.finally(() =>
					{
						this.historyLoading = false;
					});
			},
			/// Opens a history item in the operation panel (read-only for finished runs).  The current run is shown live instead.
			selectHistoryRun(item)
			{
				if (!item || !item.runId || this.selectedRunLoading)
					return;
				if (this.activeRun && this.activeRun.runId === item.runId)
				{
					this.showRun(null);
					return;
				}
				this.selectedRunLoading = item.runId;
				GetDbMaintenanceRun(item.runId)
					.then(result =>
					{
						if (result.error)
						{
							if (isNotAvailableYet(result.error))
								this.runNote = 'Run details are not available in this build yet (server: ' + result.error + ').';
							else
								toaster.Error(result.error);
							return;
						}
						if (result.run)
						{
							this.selectedRun = result.run;
							this.scrollTo('dbmRun');
						}
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					})
					.finally(() =>
					{
						this.selectedRunLoading = null;
					});
			},
			/// Shows a run (or, with null, the current operation) in the panel and scrolls to it.
			showRun(run)
			{
				this.selectedRun = run || null;
				this.scrollTo('dbmRun');
			},
			/// Opens a finished run's HTML report in a sandboxed iframe inside a modal (never injected into the page).
			showReport(item)
			{
				if (!item || !item.runId || this.anyActionInFlight)
					return;
				let subtitle = runKindLabel(item.kind) + ', ' + formatDateTime(item.startedUtc);
				this.openDialog(() => ModalDialog(RunReportPopup, { runId: item.runId, subtitle }));
			},
			// ---- Secrets -------------------------------------------------------------------------------------------
			saveSecrets()
			{
				if (!this.secretsHasChanges)
					return;
				let payload = {};
				for (let i = 0; i < this.secretFields.length; i++)
				{
					let key = this.secretFields[i].key;
					// null = leave unchanged; "" = clear; anything else = new value.
					payload[key] = this.secretsClear[key] ? "" : (this.secrets[key] !== '' ? this.secrets[key] : null);
				}
				this.secretsSaving = true;
				SetDbMaintenanceSecrets(payload)
					.then(result =>
					{
						if (result.error)
							toaster.Error(result.error);
						else
						{
							toaster.Success(result.message || 'Secrets saved.');
							this.resetSecretInputs();
						}
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					})
					.finally(() =>
					{
						this.secretsSaving = false;
						this.slideCounter++;
						this.loadStatus(false);
						this.loadConfig();
					});
			},
			resetSecretInputs()
			{
				for (let i = 0; i < this.secretFields.length; i++)
				{
					let key = this.secretFields[i].key;
					this.secrets[key] = '';
					this.secretsClear[key] = false;
				}
			},
			secretStateText(key)
			{
				if (this.secretsClear[key])
					return 'will be CLEARED';
				return this.secrets[key] !== '' ? 'will be replaced' : 'unchanged';
			},
			secretStateClass(key)
			{
				if (this.secretsClear[key])
					return 'secretStateClear';
				return this.secrets[key] !== '' ? 'secretStateSet' : 'secretStateUnchanged';
			},
			normalizeSecretName(name)
			{
				return String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
			},
			/// Maps a server-reported secret name onto the matching field label when it corresponds to one of the three known secrets.
			friendlySecretName(name)
			{
				let norm = this.normalizeSecretName(name);
				let field = this.secretFields.find(f => this.normalizeSecretName(f.key) === norm || this.normalizeSecretName(f.label) === norm);
				return field ? field.label : String(name);
			},
			isSecretMissing(field)
			{
				return this.missingSecretNames.indexOf(field.label) !== -1;
			},
			// ---- Formatting helpers --------------------------------------------------------------------------------
			formatUtc,
			formatDateTime,
			formatBytes(bytes)
			{
				if (bytes === null || typeof bytes === 'undefined' || isNaN(bytes))
					return 'unknown';
				let units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
				let value = Number(bytes);
				let i = 0;
				while (Math.abs(value) >= 1024 && i < units.length - 1)
				{
					value /= 1024;
					i++;
				}
				return (i >= 3 ? value.toFixed(1) : Math.round(value)) + ' ' + units[i];
			},
			formatElapsed,
			formatMs,
			ordinal(n)
			{
				let mod100 = n % 100;
				if (mod100 >= 11 && mod100 <= 13)
					return n + 'th';
				switch (n % 10)
				{
					case 1: return n + 'st';
					case 2: return n + 'nd';
					case 3: return n + 'rd';
					default: return n + 'th';
				}
			},
			weekOn(week)
			{
				return Array.isArray(this.schedule.weeksOfMonth) && this.schedule.weeksOfMonth.indexOf(week) !== -1;
			},
			endpointText(ep)
			{
				if (!ep || !ep.name)
					return '(not configured)';
				let text = ep.name;
				if (ep.host && ep.host !== ep.name)
					text += ' (' + ep.host + ')';
				if (ep.backupsLocalPath)
					text += ' — backups in ' + ep.backupsLocalPath;
				return text;
			},
			// ---- Web server rendering helpers ----------------------------------------------------------------------
			chipClass(ok)
			{
				return ok ? 'chipOk' : 'chipBad';
			},
			chipText(responded, healthy)
			{
				if (!responded)
					return 'no response';
				return healthy ? 'OK' : 'unhealthy';
			},
			webServerDotClass(ws)
			{
				if (!ws.connected)
					return 'dotGray';
				if (ws.agentLacksRpc)
					return 'dotAmber';
				return ws.healthy ? 'dotGreen' : 'dotRed';
			},
			webServerDotTitle(ws)
			{
				if (!ws.connected)
					return 'Agent disconnected';
				if (ws.agentLacksRpc)
					return 'Agent needs update';
				return ws.healthy ? 'Healthy' : (ws.summary || 'Unhealthy');
			},
			// ---- Database server rendering helpers -----------------------------------------------------------------
			isDesired(db)
			{
				return !!(this.platform && this.platform.desiredDbServer && db.name && db.name === this.platform.desiredDbServer);
			},
			dbStateClass(d)
			{
				if (d.informational)
					return 'stateInfo';
				return d.state === 'ONLINE' ? 'stateOk' : 'stateBad';
			},
			// ---- Run rendering helpers (shared with the dbmaintenance/ components) ---------------------------------
			runKindLabel,
			runStatusLabel,
			runStatusBadgeClass
		}
	}
</script>

<style scoped>
	/* Match the page background and padding used by other client pages (e.g. the AWS Maintenance page). */
	.dbMaintenanceRoot
	{
		padding: 8px 8px 1em 8px;
		background-color: #EFEDE6;
		overflow-y: auto;
	}

		.dbMaintenanceRoot > *
		{
			max-width: 1100px;
		}

	.pageHeading
	{
		font-size: 32px;
		margin-bottom: 0.33em;
	}

	.secondaryHeading
	{
		font-size: 24px;
		margin-bottom: 0.33em;
	}

	.card
	{
		border: 1px solid #DDDDDD;
		padding: 12px;
		background-color: #FFFFFF;
		margin-bottom: 1.5em;
		box-shadow: 2px 2px 2px rgba(0,0,0,0.4);
	}

	.errorBox
	{
		background-color: #FFE0E0;
		border: 1px solid #C00000;
		color: #660000;
		padding: 8px 12px;
		margin-bottom: 1em;
		white-space: pre-wrap;
		word-break: break-word;
	}

	.warningBox
	{
		background-color: #FFEFC9;
		border: 1px solid #E0B93F;
		color: #7A5200;
		padding: 8px 12px;
		margin: 8px 0;
	}

	/* ---- Dry-run banner and feature notices ---------------------------------------------------------------------- */
	.dryRunBanner
	{
		background: repeating-linear-gradient(135deg, #FFB347 0px, #FFB347 14px, #FFC97A 14px, #FFC97A 28px);
		border: 2px solid #A85A00;
		color: #3A2000;
		padding: 10px 14px;
		margin-bottom: 1em;
	}

	.dryRunTitle
	{
		font-weight: bold;
		font-size: 1.15em;
	}

	.dryRunDetail
	{
		margin-top: 4px;
		font-size: 0.95em;
	}

	.featureStrip
	{
		display: flex;
		flex-direction: column;
		gap: 4px;
		margin-bottom: 1em;
	}

	.featureNotice
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 6px 14px;
		padding: 6px 12px;
		border: 1px solid;
	}

		.featureNotice.noticeWarn
		{
			background-color: #FFEFC9;
			border-color: #E0B93F;
			color: #7A5200;
		}

		.featureNotice.noticeBad
		{
			background-color: #FFE0E0;
			border-color: #C00000;
			color: #660000;
		}

	.featureNoticeLink
	{
		font-weight: bold;
		color: inherit;
	}

	/* ---- Toolbar ---------------------------------------------------------------------------------------------------- */
	.toolbarCard
	{
		display: flex;
		flex-wrap: wrap;
		gap: 8px 14px;
		align-items: center;
	}

	.versionText
	{
		margin-left: auto;
		font-size: 0.85em;
	}

	.loadingRow,
	.loadingInline
	{
		display: inline-flex;
		align-items: center;
		gap: 10px;
	}

	.loadingRow
	{
		display: flex;
		padding: 8px;
	}

	.hintText
	{
		color: #555555;
		font-size: 0.92em;
		margin: 0 0 10px 0;
	}

	.mutedText
	{
		color: #888888;
	}

	.readOnlyBadge
	{
		font-size: 0.85em;
		padding: 1px 8px;
		border-radius: 9px;
		background-color: #EEEEEE;
		color: #666666;
		cursor: help;
	}

	code.inline
	{
		font-family: Consolas, monospace;
		background-color: #F4F4F4;
		border-radius: 3px;
		padding: 0px 5px;
	}

		code.inline.activeMismatch
		{
			background-color: #FFE0E0;
			color: #8A0000;
			font-weight: bold;
		}

	/* ---- Verdict ---------------------------------------------------------------------------------------------------- */
	.verdictCard
	{
		border-left-width: 8px;
	}

		.verdictCard.verdictGood
		{
			border-left-color: #2E9E2E;
		}

		.verdictCard.verdictWarn
		{
			border-left-color: #E0B93F;
		}

		.verdictCard.verdictBad
		{
			border-left-color: #CC3333;
		}

		.verdictCard.verdictUnknown
		{
			border-left-color: #AAAAAA;
		}

	.verdictRow
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px 16px;
		margin-bottom: 8px;
	}

	.verdictBadge
	{
		display: inline-block;
		padding: 4px 16px;
		border-radius: 14px;
		font-size: 1.4em;
		font-weight: bold;
		white-space: nowrap;
	}

	.verdictDetail
	{
		font-size: 1.05em;
	}

	.desiredLine
	{
		font-size: 1.05em;
		margin: 6px 0;
		line-height: 1.6;
	}

	.recommendedRow
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px 14px;
		margin: 10px 0 4px 0;
	}

	.primaryActionButton
	{
		font-size: 1.1em;
	}

	.disabledReason
	{
		color: #7A5200;
		font-size: 0.9em;
		font-style: italic;
	}

	.warningList
	{
		margin: 8px 0 0 0;
		padding-left: 22px;
		color: #7A5200;
	}

	.envLine
	{
		margin-top: 6px;
		font-size: 0.88em;
	}

	/* ---- Needs-attention banner ---------------------------------------------------------------------------------- */
	.attentionBanner
	{
		background-color: #FFE0E0;
		border: 3px solid #C00000;
		color: #4A0000;
		padding: 10px 14px;
		margin-bottom: 1em;
	}

	.attentionTitle
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px 12px;
		font-weight: bold;
		font-size: 1.15em;
	}

	.attentionBody
	{
		margin-top: 6px;
		line-height: 1.5;
	}

	.attentionFix
	{
		font-family: inherit;
		white-space: pre-wrap;
		word-break: break-word;
		background-color: #FFFFFF;
		border: 1px solid #CC6666;
		padding: 8px 12px;
		margin: 8px 0;
		line-height: 1.5;
	}

	.attentionActions
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px 16px;
		margin-top: 6px;
	}

	.attentionLink
	{
		color: #4A0000;
		font-weight: bold;
		font-size: 1em;
	}

	.attentionActions :deep(.slideunlock)
	{
		margin: 0;
	}

	/* ---- Run / lock ---------------------------------------------------------------------------------------------- */
	.runCard
	{
		border-left: 8px solid #3A7BD5;
	}

		.runCard.runCardIdle
		{
			border-left-color: #AAAAAA;
		}

		.runCard.runCardAttention
		{
			border-left-color: #CC3333;
		}

	.idleText
	{
		padding: 4px 0;
	}

	.noteBox
	{
		background-color: #F4F4F4;
		border: 1px solid #DDDDDD;
		color: #555555;
		padding: 8px 12px;
		margin: 8px 0;
	}

	.lockBlock
	{
		margin-top: 12px;
		padding: 8px 12px;
		border: 1px solid #C9D8EA;
		border-radius: 4px;
		background-color: #F4F7FB;
	}

		.lockBlock.lockStale
		{
			border-color: #CC6666;
			background-color: #FFF0F0;
		}

	.lockHead
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 6px 10px;
		font-size: 1.05em;
	}

	.lockGrid
	{
		font-size: 0.95em;
	}

	.lockOtherInstance
	{
		margin: 8px 0 0 0;
	}

	.lockOverrideRow
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px 14px;
		margin-top: 8px;
	}

	.stateBadge.big
	{
		font-size: 1.05em;
		padding: 3px 14px;
	}

	.badgeHold
	{
		background-color: #EADDFF;
		color: #3F1F7A;
		border: 1px solid #B39DE0;
	}

	.kvGrid
	{
		display: grid;
		grid-template-columns: max-content 1fr;
		gap: 4px 14px;
		align-items: baseline;
		margin: 6px 0;
	}

	.kvKey
	{
		font-weight: bold;
		color: #444444;
		white-space: nowrap;
	}

	/* ---- Web servers --------------------------------------------------------------------------------------------- */
	.aggregateRow
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px 14px;
		padding: 8px 6px;
		font-size: 1.05em;
	}

	.aggregateText
	{
		font-weight: bold;
	}

	.linkButton
	{
		background: none;
		border: none;
		padding: 0;
		color: #1A5FB4;
		text-decoration: underline;
		cursor: pointer;
		font: inherit;
		font-size: 0.9em;
	}

	.serverList
	{
		margin-top: 6px;
	}

	.serverRow
	{
		padding: 12px 6px;
		border-bottom: 1px solid #E4E4E4;
	}

		.serverRow:last-child
		{
			border-bottom: none;
		}

	.serverHead
	{
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
	}

	.serverName
	{
		font-weight: bold;
		font-size: 1.15em;
	}

	.serverConfig
	{
		margin: 6px 0 0 18px;
		line-height: 1.7;
	}

	.configMismatchText
	{
		color: #8A0000;
		font-weight: bold;
	}

	.healthChips
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 6px 8px;
		margin: 8px 0 0 18px;
	}

	.chip
	{
		display: inline-block;
		padding: 1px 9px;
		border-radius: 9px;
		font-size: 0.85em;
		border: 1px solid;
		white-space: nowrap;
	}

		.chip.chipOk
		{
			background-color: #D4F5D4;
			color: #0A5A0A;
			border-color: #77CC77;
		}

		.chip.chipBad
		{
			background-color: #FFE0E0;
			color: #8A0000;
			border-color: #CC6666;
			font-weight: bold;
		}

	.problemList
	{
		margin: 8px 0 0 18px;
		padding-left: 22px;
		color: #8A0000;
	}

	.problemText
	{
		color: #8A0000;
		font-weight: bold;
	}

	.serverMeta
	{
		font-size: 0.85em;
		margin: 6px 0 0 18px;
	}

	.statusDot
	{
		flex: 0 0 auto;
		display: inline-block;
		width: 10px;
		height: 10px;
		border-radius: 50%;
		cursor: help;
	}

	.dotGreen
	{
		background-color: #2E9E2E;
	}

	.dotAmber
	{
		background-color: #E0B93F;
	}

	.dotRed
	{
		background-color: #CC3333;
	}

	.dotGray
	{
		background-color: #AAAAAA;
	}

	.stateBadge
	{
		display: inline-block;
		padding: 2px 10px;
		border-radius: 10px;
		font-weight: bold;
		font-size: 0.9em;
		white-space: nowrap;
		cursor: help;
	}

	.badgeGood
	{
		background-color: #D4F5D4;
		color: #0A5A0A;
		border: 1px solid #77CC77;
	}

	.badgeWarn
	{
		background-color: #FFEFC9;
		color: #7A5200;
		border: 1px solid #E0B93F;
	}

	.badgeBad
	{
		background-color: #FFE0E0;
		color: #8A0000;
		border: 1px solid #CC6666;
	}

	.badgeInfo
	{
		background-color: #DDEBFF;
		color: #123F7A;
		border: 1px solid #7FAEE8;
	}

	.badgeOff
	{
		background-color: #EEEEEE;
		color: #666666;
		border: 1px solid #CCCCCC;
	}

	.badgeActive
	{
		background-color: #3A7BD5;
		color: #FFFFFF;
		border: 1px solid #1F5AA8;
	}

	.badgeLatent
	{
		background-color: #CC3333;
		color: #FFFFFF;
		border: 1px solid #8A0000;
	}

	/* ---- Database servers ---------------------------------------------------------------------------------------- */
	.dbServerGrid
	{
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
		gap: 12px;
	}

	.dbServerCard
	{
		border: 1px solid #E0E0E0;
		border-radius: 4px;
		padding: 10px 12px;
		background-color: #FAFAFA;
	}

		.dbServerCard.dbActive
		{
			border-color: #3A7BD5;
			box-shadow: inset 0 0 0 1px #3A7BD5;
		}

	.dbServerHead
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		margin-bottom: 4px;
	}

	.roleTag
	{
		text-transform: uppercase;
		letter-spacing: 0.08em;
		font-size: 0.8em;
		color: #666666;
		font-weight: bold;
	}

	.hostLine
	{
		font-size: 0.9em;
		margin-bottom: 4px;
	}

	.agentDetail
	{
		font-family: Consolas, monospace;
		font-size: 0.85em;
		background-color: #F4F4F4;
		border: 1px solid #E0E0E0;
		padding: 4px 8px;
		margin: 4px 0 0 0;
		white-space: pre-wrap;
		word-break: break-word;
		color: #8A0000;
	}

	.freeSpaceLow
	{
		color: #8A0000;
		font-weight: bold;
	}

	.dbTable
	{
		border-collapse: collapse;
		width: 100%;
		margin-top: 8px;
		font-size: 0.95em;
	}

		.dbTable th,
		.dbTable td
		{
			text-align: left;
			padding: 4px 8px;
			border-bottom: 1px solid #E4E4E4;
		}

		.dbTable th
		{
			color: #555555;
			font-weight: bold;
			font-size: 0.9em;
		}

		.dbTable tr.dbInformational td
		{
			color: #999999;
		}

	.dbState
	{
		font-weight: bold;
	}

		.dbState.stateOk
		{
			color: #0A5A0A;
		}

		.dbState.stateBad
		{
			color: #8A0000;
		}

		.dbState.stateInfo
		{
			color: #999999;
			font-weight: normal;
		}

	/* ---- Configure the migration ------------------------------------------------------------------------------- */
	.formRow
	{
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
		margin: 8px 0;
	}

	.directionSelect
	{
		padding: 5px 8px;
		border: 1px solid #AAAAAA;
		border-radius: 4px;
		font-size: 15px;
		min-width: 200px;
		background-color: #FFFFFF;
	}

	.swapButton
	{
		font-size: 18px;
		line-height: 1;
		padding: 4px 10px;
		border: 1px solid #AAAAAA;
		border-radius: 4px;
		background-color: #F4F4F4;
		cursor: pointer;
	}

		.swapButton:disabled
		{
			opacity: 0.55;
			cursor: not-allowed;
		}

	.numberInput
	{
		width: 90px;
		padding: 5px 8px;
		border: 1px solid #AAAAAA;
		border-radius: 4px;
		font-size: 15px;
	}

	.dbListHead
	{
		margin-bottom: 2px;
	}

	.dbListNote
	{
		font-size: 0.9em;
		margin-bottom: 4px;
	}

	.dbCheckList
	{
		display: flex;
		flex-wrap: wrap;
		gap: 6px 14px;
		margin: 4px 0 8px 0;
	}

	.dbCheckItem
	{
		display: inline-flex;
		align-items: center;
		gap: 6px;
		cursor: pointer;
		padding: 3px 10px;
		border: 1px solid #E0E0E0;
		border-radius: 4px;
		background-color: #FAFAFA;
	}

		.dbCheckItem.dbCheckDisabled
		{
			cursor: default;
			color: #777777;
		}

	.dbCheckName
	{
		font-weight: bold;
	}

	.dbWebAppTag
	{
		font-size: 11px;
		line-height: 16px;
		padding: 0 5px;
		border-radius: 3px;
		border: 1px solid #B7C7E0;
		background-color: #EEF3FA;
		color: #2B4C7E;
		white-space: nowrap;
	}

	.advancedLabel
	{
		cursor: pointer;
	}

	/* ---- Preconditions ------------------------------------------------------------------------------------------- */
	.preHeader
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px 12px;
		padding: 8px 10px;
		border-left: 6px solid #AAAAAA;
		background-color: #FAFAFA;
		margin: 8px 0 4px 0;
	}

		.preHeader.preHeaderPass
		{
			border-left-color: #2E9E2E;
		}

		.preHeader.preHeaderWarn
		{
			border-left-color: #E0B93F;
			background-color: #FFF8E6;
		}

		.preHeader.preHeaderFail
		{
			border-left-color: #CC3333;
			background-color: #FFF0F0;
		}

	.preSummary
	{
		font-weight: bold;
	}

	.preMeta
	{
		font-size: 0.9em;
		margin-bottom: 6px;
	}

	.preStale
	{
		color: #7A5200;
		font-weight: bold;
		margin-left: 6px;
	}

	.preGroup
	{
		margin-top: 10px;
	}

	.preGroupTitle
	{
		display: flex;
		flex-wrap: wrap;
		gap: 6px 12px;
		align-items: baseline;
		font-weight: bold;
		font-size: 1.05em;
		border-bottom: 1px solid #E0E0E0;
		padding-bottom: 3px;
		margin-bottom: 4px;
	}

	.preGroupCounts
	{
		font-size: 0.85em;
		font-weight: normal;
	}

	.preGroupTime
	{
		cursor: help;
		text-decoration: underline dotted;
	}

	.preRow
	{
		display: flex;
		gap: 10px;
		padding: 6px 4px;
		border-bottom: 1px solid #F0F0F0;
	}

		.preRow:last-child
		{
			border-bottom: none;
		}

		.preRow.prewarn
		{
			background-color: #FFF8E6;
		}

		.preRow.prefail
		{
			background-color: #FFF0F0;
		}

	.preIcon
	{
		flex: 0 0 22px;
		width: 22px;
		height: 22px;
		line-height: 22px;
		text-align: center;
		border-radius: 50%;
		font-weight: bold;
		color: #FFFFFF;
		background-color: #AAAAAA;
		cursor: help;
	}

	.prepass .preIcon
	{
		background-color: #2E9E2E;
	}

	.prewarn .preIcon
	{
		background-color: #E0B93F;
		color: #3A2000;
	}

	.prefail .preIcon
	{
		background-color: #CC3333;
	}

	.preBody
	{
		flex: 1 1 auto;
		min-width: 0;
	}

	.preLine
	{
		display: flex;
		flex-wrap: wrap;
		gap: 4px 10px;
		align-items: baseline;
	}

	.preName
	{
		font-weight: bold;
	}

	.prewarn .preMessage
	{
		color: #7A5200;
	}

	.prefail .preMessage
	{
		color: #8A0000;
		font-weight: bold;
	}

	.preFoot
	{
		display: flex;
		gap: 12px;
		font-size: 0.85em;
		margin-top: 2px;
	}

	.preDetail
	{
		font-family: Consolas, monospace;
		font-size: 0.85em;
		background-color: #F4F4F4;
		border: 1px solid #E0E0E0;
		padding: 6px 8px;
		margin: 4px 0 0 0;
		white-space: pre-wrap;
		word-break: break-word;
	}

	/* ---- Actions ------------------------------------------------------------------------------------------------- */
	.actionGrid
	{
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
		gap: 12px;
	}

	.actionItem
	{
		border: 1px solid #E8E8E8;
		border-radius: 4px;
		padding: 10px;
		background-color: #FAFAFA;
	}

		.actionItem.actionReady
		{
			border-color: #77CC77;
			background-color: #F6FBF6;
		}

		.actionItem.actionPrimary.actionReady
		{
			box-shadow: inset 0 0 0 1px #77CC77;
		}

	.actionButtonRow
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px 12px;
	}

	.previewKindSelect
	{
		padding: 4px 8px;
		border: 1px solid #AAAAAA;
		border-radius: 4px;
		font-size: 14px;
		background-color: #FFFFFF;
	}

	.actionDesc
	{
		color: #555555;
		font-size: 0.9em;
		margin: 6px 0 4px 0;
	}

	.actionNote
	{
		font-size: 0.85em;
		margin-bottom: 4px;
	}

	.readyReason
	{
		color: #0A5A0A;
		font-size: 0.9em;
	}

	.gradientButton:disabled
	{
		opacity: 0.55;
		cursor: not-allowed;
	}

	/* ---- Secrets ------------------------------------------------------------------------------------------------- */
	.secretRow
	{
		display: grid;
		grid-template-columns: max-content minmax(200px, 320px) max-content max-content max-content;
		gap: 4px 12px;
		align-items: center;
		padding: 8px 0;
		border-bottom: 1px solid #F0F0F0;
	}

		.secretRow:last-of-type
		{
			border-bottom: none;
		}

	.formLabel
	{
		font-weight: bold;
	}

	.secretInput
	{
		padding: 5px 8px;
		border: 1px solid #AAAAAA;
		border-radius: 4px;
		font-size: 15px;
		width: 100%;
		box-sizing: border-box;
	}

		.secretInput:disabled
		{
			background-color: #F4F4F4;
			color: #999999;
		}

	.clearLabel
	{
		white-space: nowrap;
		cursor: pointer;
		font-size: 0.9em;
	}

	.secretState
	{
		font-size: 0.85em;
		white-space: nowrap;
	}

		.secretState.secretStateUnchanged
		{
			color: #888888;
		}

		.secretState.secretStateSet
		{
			color: #0A5A0A;
			font-weight: bold;
		}

		.secretState.secretStateClear
		{
			color: #8A0000;
			font-weight: bold;
		}

	.secretHint
	{
		grid-column: 1 / -1;
		font-size: 0.85em;
	}

	.secretsCommitRow
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px 14px;
		margin: 12px 0 6px 0;
	}

	.secretsMeta
	{
		font-size: 0.9em;
	}

	/* ---- Configuration ------------------------------------------------------------------------------------------- */
	.configColumns
	{
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
		gap: 12px 24px;
	}

	.configTitle
	{
		font-weight: bold;
		font-size: 1.05em;
		border-bottom: 1px solid #E0E0E0;
		padding-bottom: 3px;
		margin-bottom: 4px;
	}

	.weekChips
	{
		display: inline-flex;
		flex-wrap: wrap;
		gap: 6px;
	}

	.weekChip
	{
		display: inline-block;
		padding: 1px 8px;
		border-radius: 9px;
		font-size: 0.85em;
		border: 1px solid;
		white-space: nowrap;
	}

		.weekChip.weekOn
		{
			background-color: #D4F5D4;
			color: #0A5A0A;
			border-color: #77CC77;
		}

		.weekChip.weekOff
		{
			background-color: #EEEEEE;
			color: #999999;
			border-color: #CCCCCC;
		}
</style>
